(() => {
  'use strict';

  const money = (cents, format) => {
    const value = (cents / 100).toFixed(2).replace('.', ',');
    // the € keeps to its amount: a line never ends on «24,90» with «€» on the next one
    return (format || '{{amount}} €').replace(/\{\{\s*amount[a-z_]*\s*\}\}/, value).replace(/ (?=€)/, '\u00a0');
  };

  const toast = (html) => {
    const region = document.querySelector('[data-toast-region]');
    if (!region) return;
    const el = document.createElement('div');
    el.className = 'toast';
    el.innerHTML = html;
    region.appendChild(el);
    setTimeout(() => {
      el.style.opacity = '0';
      el.style.transition = 'opacity 200ms';
      setTimeout(() => el.remove(), 220);
    }, 3600);
  };

  const syncCartCount = (cart) => {
    if (typeof cart?.item_count !== 'number') return;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    // every cart change passes here with the whole cart: the product page's sticky bar listens (in the cart → «Zur Kasse»)
    if (Array.isArray(cart.items)) document.dispatchEvent(new CustomEvent('df:cart', { detail: cart }));
  };

  // The cart queue lives in cart-integration.js. If that one file did not load, the buttons must not freeze:
  // the fallback runs each operation at once and skips the Shopify events (theme-code review №16).
  const cartQueue = () => window.DrFussCart || { run: (operation) => Promise.resolve().then(operation), begin: () => ({ resolve() {}, reject() {} }) };

  const updateCartCount = async () => {
    try {
      const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
      syncCartCount(cart);
      return cart;
    } catch {
      return null;
    }
  };

  // Pack sets (snippets/pack-map): the cart holds the price the product page promises. Identical packs (same
  // product, same size) become the shop's sets — 2 and 3 as the set, 4 as 2 + 2, 5 as 3 + 2; from 6 packs in the cart
  // every set is opened into single packs, because the automatic −40 % counts single packs only. With the shop's line
  // discounts on (map.mix) the single packs left of a line — sizes and colours mixed — cost the line's sets too; only a
  // lone single beside a set of its line would stay dear (set + 24,90 €), so that line opens into single packs.
  let packMapCache;
  const packMap = () => {
    if (packMapCache === undefined) {
      try {
        packMapCache = JSON.parse(document.querySelector('[data-pack-map]')?.textContent || 'null');
      } catch {
        packMapCache = null;
      }
    }
    return packMapCache;
  };
  // the cheapest split of t identical packs into sets of 3, sets of 2 and single packs (sets only when in stock)
  const packSplit = (t, s) => {
    const b2 = s['2'] && s['2'].ok ? s['2'] : null;
    const b3 = s['3'] && s['3'].ok ? s['3'] : null;
    let best = { 1: t, 2: 0, 3: 0, cost: t * s.price };
    for (let k3 = 0; k3 * 3 <= t && (k3 === 0 || b3); k3 += 1) {
      for (let k2 = 0; k3 * 3 + k2 * 2 <= t && (k2 === 0 || b2); k2 += 1) {
        const k1 = t - k3 * 3 - k2 * 2;
        const cost = k1 * s.price + (k2 ? k2 * b2.price : 0) + (k3 ? k3 * b3.price : 0);
        if (cost < best.cost) best = { 1: k1, 2: k2, 3: k3, cost };
      }
    }
    return best;
  };
  // { updates: { variantId (or line key): quantity }, adds: [{ id, quantity }] } that brings the cart to the cheapest form,
  // or null when it already is. A subscription line (selling_plan_allocation) keeps its form and does not count: Shopify
  // sells no subscription on its bundles, the set price on subscription comes from the subscription discounts
  // (snippets/purchase-options), and the −40 % from 6 packs is for one-time packs. The subscription line is never touched:
  // where a variant is in the cart both once and on subscription, the one-time line is updated by its line key; where its
  // only line is the subscription, the one-time packs it should get are added as a new line (cart/add.js without a plan),
  // because cart/update.js by variant id would change the subscription line.
  const packPlan = (cart, map) => {
    const have = {};
    const groups = {};
    const keyOf = {};
    const onSub = new Set();
    let total = 0;
    (cart.items || []).forEach((it) => {
      const id = String(it.variant_id);
      if (it.selling_plan_allocation) {
        onSub.add(id);
        return;
      }
      if (it.key) keyOf[id] = it.key;
      const single = map.s[id] ? id : map.b[id] ? String(map.b[id].s) : null;
      if (!single || !map.s[single]) return;
      const packs = map.s[id] ? it.quantity : it.quantity * map.b[id].n;
      have[id] = (have[id] || 0) + it.quantity;
      groups[single] = (groups[single] || 0) + packs;
      total += packs;
    });
    const open = map.pct > 0 && total >= map.min;
    const splits = {};
    Object.entries(groups).forEach(([id, t]) => {
      splits[id] = open ? { 1: t, 2: 0, 3: 0 } : packSplit(t, map.s[id]);
    });
    if (!open && map.mix) {
      const lines = {};
      Object.entries(splits).forEach(([id, split]) => {
        const key = map.s[id].l || id;
        const line = lines[key] || (lines[key] = { singles: 0, sets: 0, ids: [] });
        line.singles += split[1];
        line.sets += split[2] + split[3];
        line.ids.push(id);
      });
      Object.values(lines).forEach((line) => {
        if (line.singles !== 1 || !line.sets || !line.ids.every((id) => map.s[id].ok)) return;
        line.ids.forEach((id) => { splits[id] = { 1: groups[id], 2: 0, 3: 0 }; });
      });
    }
    const want = {};
    Object.entries(groups).forEach(([id]) => {
      const s = map.s[id];
      const ids = [id, s['2']?.id, s['3']?.id].filter(Boolean).map(String);
      if (open && !s.ok) {
        ids.forEach((x) => { want[x] = have[x] || 0; });
        return;
      }
      const split = splits[id];
      want[id] = split[1];
      if (s['2']) want[String(s['2'].id)] = split[2];
      if (s['3']) want[String(s['3'].id)] = split[3];
    });
    const updates = {};
    const adds = [];
    new Set([...Object.keys(have), ...Object.keys(want)]).forEach((id) => {
      const w = want[id] || 0;
      const h = have[id] || 0;
      if (w === h) return;
      if (onSub.has(id) && !keyOf[id]) {
        if (w > h) adds.push({ id: Number(id), quantity: w - h });
        return;
      }
      updates[onSub.has(id) ? keyOf[id] : id] = w;
    });
    // the packs only change form (a set opens into singles or the other way), so an add always comes with an update;
    // without one nothing is changed
    return Object.keys(updates).length ? { updates, adds } : null;
  };
  // after a cart change: bring the packs to the cheapest form; the update's response (with the asked sections) or null
  const settlePacks = async (sections) => {
    const map = packMap();
    if (!map) return null;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cart = await (await fetch(`${root}cart.js`)).json();
      const plan = packPlan(cart, map);
      if (!plan) return null;
      const post = (path, body) => fetch(`${root}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      // the new one-time lines first: if they cannot be added, the sets they replace stay in the cart
      if (plan.adds.length && !(await post('cart/add.js', { items: plan.adds })).ok) return null;
      const res = await post('cart/update.js', { updates: plan.updates, sections });
      return res.ok ? await res.json() : null;
    } catch {
      return null;
    }
  };

  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.drawer = document.getElementById(this.getAttribute('target'));
      if (!this.button || !this.drawer) return;
      this.button.addEventListener('click', () => this.toggle());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.toggle(false);
      });
    }
    toggle(force) {
      const open = typeof force === 'boolean' ? force : this.drawer.hasAttribute('hidden');
      const focusInside = this.drawer.contains(document.activeElement);
      this.drawer.toggleAttribute('hidden', !open);
      // closing from inside the menu (Escape) returns the keyboard to the «Menu» button, not into the hidden list
      if (!open && focusInside) this.button.focus();
      this.button.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.button.querySelectorAll('[data-icon]').forEach((i) => {
        i.hidden = i.dataset.icon === (open ? 'menu' : 'close');
      });
    }
  }
  customElements.define('menu-drawer', MenuDrawer);

  // Shared price lockup (deck Parts 2–3, 26.08.2026): the struck-through previous
  // price above the total, the saving in green, the percentage as a red chip. packs —
  // the number of packs in the tier; per-pair is computed from the full total and all pairs in the order.
  // The tax and shipping line under the price follows the shown total: the country's fee below its free-shipping
  // threshold, «free shipping» from it (sections/main-product.liquid, block price).
  const syncPriceNote = (root, totalCents) => {
    root.querySelectorAll('[data-price-note]').forEach((note) => {
      const from = parseInt(note.dataset.freeFrom, 10) || 0;
      const free = from > 0 && totalCents >= from;
      const feeEl = note.querySelector('[data-note-fee]');
      const freeEl = note.querySelector('[data-note-free]');
      if (feeEl) feeEl.hidden = free;
      if (freeEl) freeEl.hidden = !free;
    });
  };

  const updatePriceLockup = (root, priceCents, compareCents, packs = 1) => {
    const priceEls = root.querySelectorAll('[data-price]');
    const fmt = priceEls[0]?.dataset.moneyFormat;
    priceEls.forEach((el) => {
      el.textContent = money(priceCents, el.dataset.moneyFormat);
    });
    root.querySelectorAll('[data-price-from]').forEach((el) => {
      el.hidden = true;
    });
    root.querySelectorAll('[data-price-per-pair]').forEach((el) => {
      const pairs = parseInt(el.dataset.pairs, 10);
      if (pairs > 1) {
        const perPair = money(Math.round(priceCents / (pairs * packs)), fmt);
        el.textContent = el.dataset.perPairTemplate.replace('[price]', perPair);
      }
    });
    const has = compareCents > priceCents;
    root.querySelectorAll('[data-price-compare]').forEach((el) => {
      el.hidden = !has;
      if (has) el.textContent = money(compareCents, el.dataset.moneyFormat || fmt);
    });
    root.querySelectorAll('[data-price-pct]').forEach((el) => {
      el.hidden = !has;
      if (has) el.textContent = `−${Math.round(((compareCents - priceCents) / compareCents) * 100)} %`;
    });
    root.querySelectorAll('[data-price-save]').forEach((el) => {
      el.hidden = !has;
      if (has && el.dataset.saveTemplate) el.textContent = el.dataset.saveTemplate.replace('[amount]', money(compareCents - priceCents, fmt));
    });
    syncPriceNote(root, priceCents);
  };

  // One CTA state machine (Р-117): until a size is chosen — "Choose size",
  // until a tier is chosen — "Choose pack". The sticky button stays active:
  // its click is intercepted by the guard and scrolls to the choice.
  const syncCta = (root) => {
    const hasVariants = !!root.querySelector('variant-picker');
    const tierPicker = root.querySelector('tier-picker');
    const sizeOk = !hasVariants || root.dataset.sizeOk === '1';
    const sizeSoldOut = root.dataset.sizeSoldOut === '1';
    const tierOk = !tierPicker || !!tierPicker.chosen;
    root.querySelectorAll('[data-add-button]').forEach((button) => {
      const sticky = button.hasAttribute('data-sticky-add-button');
      // The sticky bar's third state (the owner's words 16.10.2026): this pack is in the cart and nothing was chosen
      // anew since — «Zur Kasse», a plain post to the checkout (form StickyCheckout, sections/main-product); a new
      // size or pack brings back the steps above.
      if (sticky && button.dataset.textCheckout) {
        if (!button.dataset.form) button.dataset.form = button.getAttribute('form') || '';
        const toCheckout = root.dataset.inCart === '1' && root.dataset.choiceFresh !== '1';
        button.dataset.mode = toCheckout ? 'checkout' : '';
        button.setAttribute('form', toCheckout ? 'StickyCheckout' : button.dataset.form);
        if (toCheckout) {
          button.name = 'checkout';
          button.disabled = false;
          button.removeAttribute('aria-disabled');
          button.textContent = button.dataset.textCheckout;
          return;
        }
        button.removeAttribute('name');
      }
      // Before a choice the button stays red and pressable (aria-disabled): pressing it shows what is missing,
      // a pale disabled button reads poorly and answers nothing (review/audit-2026-10-01/cro.md, row 5).
      if (sizeSoldOut) {
        button.disabled = true;
        button.removeAttribute('aria-disabled');
        button.textContent = button.dataset.textSoldOut;
      } else if (!sizeOk) {
        button.disabled = false;
        if (!sticky) button.setAttribute('aria-disabled', 'true');
        button.textContent = button.dataset.textChooseSize || button.dataset.textAdd;
      } else if (!tierOk) {
        button.disabled = false;
        if (!sticky) button.setAttribute('aria-disabled', 'true');
        button.textContent = button.dataset.textChoosePack || button.dataset.textAdd;
      } else {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        let label = button.dataset.textAdd;
        if (!sticky && tierPicker && tierPicker.chosen && button.dataset.textAddTotal) {
          const { n, pct } = tierPicker.chosen;
          const total = tierPicker.cartTotal ? tierPicker.cartTotal(n, pct) : tierPicker.tierTotal(n, pct);
          label = button.dataset.textAddTotal.replace('[price]', money(total, tierPicker.fmt));
        }
        button.textContent = label;
      }
    });
    // The line «what is still missing» follows every choice: it never asks for a size that is already chosen
    // (a11y №4); it stays in the page as an empty live region, so screen readers announce each new text.
    const note = root.querySelector('[data-need-message]');
    const form = root.querySelector('product-form');
    if (sizeOk && tierOk) {
      if (note) note.textContent = '';
      root.querySelectorAll('.pdp__option.is-needed').forEach((el) => el.classList.remove('is-needed'));
    } else if (note && note.textContent && form) {
      note.textContent = !sizeOk ? form.dataset.needSize : form.dataset.needPack;
    }
  };

  // Shows the shopper what is still missing: scrolls to the size or the pack choice, outlines it, says it in words.
  const showNeed = (root) => {
    const picker = root.querySelector('variant-picker');
    const needsSize = picker && root.dataset.sizeOk !== '1';
    const tierPicker = root.querySelector('tier-picker');
    const target = needsSize ? picker.querySelector('.pdp__option') : tierPicker && tierPicker.querySelector('.pdp__option');
    if (!target) return false;
    const form = root.querySelector('product-form');
    const note = root.querySelector('[data-need-message]');
    if (note && form) note.textContent = needsSize ? form.dataset.needSize : form.dataset.needPack;
    root.querySelectorAll('.pdp__option.is-needed').forEach((el) => el.classList.remove('is-needed'));
    target.classList.add('is-needed');
    clearTimeout(target._needTimer);
    target._needTimer = setTimeout(() => target.classList.remove('is-needed'), 2600);
    target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    target.querySelector('input')?.focus({ preventScroll: true });
    return true;
  };

  // A sellable variant is "in stock" even at 0 or below when Shopify allows continuing to sell.
  // This is the owner's rule of 07.10.2026: use available, never invent a quantity threshold here.
  const syncVariantAvailability = (root, variant) => {
    const canBuy = !!(variant && variant.available);
    root.querySelectorAll('[data-availability]').forEach((line) => {
      line.hidden = !variant;
      line.dataset.available = canBuy ? 'true' : 'false';
      const text = line.querySelector('[data-availability-text]');
      if (text) text.textContent = canBuy ? line.dataset.textInStock : line.dataset.textOutOfStock;
    });
    root.querySelectorAll('[data-dispatch-rule]').forEach((line) => { line.hidden = !canBuy; });
  };

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-variants]');
      if (!dataEl) return;
      this.variants = JSON.parse(dataEl.textContent);
      this.form = this.closest('form') || document.getElementById(this.getAttribute('form'));
      this.addEventListener('change', () => this.update());
    }
    selectedOptions() {
      const groups = [...this.querySelectorAll('fieldset')];
      return groups.map((g) => g.querySelector('input:checked')?.value);
    }
    update() {
      const opts = this.selectedOptions();
      // The step label shows what was chosen: «2 · Größe: 43–46» (the owner's word, 06.10.2026).
      this.querySelectorAll('fieldset').forEach((group, i) => {
        const out = group.querySelector('[data-step-value]');
        if (!out) return;
        out.textContent = opts[i] ? ': ' + opts[i].replace(/-/g, '–') : '';
        out.hidden = !opts[i];
      });
      // the sticky bar names the chosen size next to the product (the owner's word: the bar may grow to show it)
      const stickySize = document.querySelector('[data-sticky-size]');
      if (stickySize) {
        const chosen = opts.filter(Boolean).map((o) => o.replace(/-/g, '–')).join(' · ');
        stickySize.textContent = chosen ? stickySize.dataset.template.replace('[size]', chosen) : '';
        stickySize.hidden = !chosen;
      }
      const root = this.closest('[data-product-root]') || document;
      const idInput = (this.form || root).querySelector('input[name="id"]');
      if (opts.some((o) => !o)) {
        // Nothing is preselected (Р-117): some options are still unchosen.
        if (idInput) idInput.value = '';
        root.dataset.sizeOk = '';
        root.dataset.sizeSoldOut = '';
        syncVariantAvailability(root, null);
        syncCta(root);
        return;
      }
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      if (!variant) {
        syncVariantAvailability(root, null);
        root.dataset.sizeOk = '';
        root.dataset.sizeSoldOut = '';
        root.querySelectorAll('[data-add-button]').forEach((button) => {
          button.disabled = true;
          button.textContent = button.dataset.textUnavailable;
        });
        return;
      }
      if (idInput) idInput.value = variant.id;
      const skuEl = root.querySelector('[data-sku]');
      if (skuEl && variant.sku) skuEl.textContent = variant.sku;
      const tierPicker = root.querySelector('tier-picker');
      if (tierPicker) {
        // The PDP price stays "from …" until a tier is chosen; the tiers
        // are recalculated from the chosen variant's price (and follow its size for the bundles).
        if (tierPicker.setVariant) tierPicker.setVariant(variant);
        else tierPicker.setBase(variant.price);
      } else {
        updatePriceLockup(root, variant.price, variant.compare_at_price || 0);
      }
      root.dataset.sizeOk = '1';
      root.dataset.sizeSoldOut = variant.available ? '' : '1';
      syncVariantAvailability(root, variant);
      syncCta(root);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }
  customElements.define('variant-picker', VariantPicker);

  // Pack ladder (26.08.2026): 1 / 2 → −30 % / 3 → −35 %, nothing
  // preselected (Р-117). Choosing a tier sets the quantity and recalculates
  // the price lockup; the actual discount in the cart must come from the
  // automatic quantity discount in the admin (honesty gate in the schema).
  class TierPicker extends HTMLElement {
    connectedCallback() {
      this.base = parseInt(this.dataset.base, 10) || 0;
      this.fmt = this.dataset.moneyFormat;
      this.perTemplate = this.dataset.perTemplate || '';
      this.pairs = parseInt(this.dataset.pairs, 10) || 0;
      this.freeFrom = parseInt(this.dataset.freeFrom, 10) || 0;
      // packs of 2 and 3 as bundle products with set prices (custom.pack_bundles): {"2": {size: {id, price, available}}, "3": …}
      try {
        this.bundles = this.dataset.bundles ? JSON.parse(this.dataset.bundles) : null;
      } catch {
        this.bundles = null;
      }
      this.size = this.dataset.size || '';
      this.single = this.dataset.single || '';
      // a 2- or 3-pack set's own page (snippets/pack-tiers, data-set = its packs): the rungs are priced by setTotal
      this.setPacks = parseInt(this.dataset.set, 10) || 0;
      this.chosen = null;
      // one-time or subscription (snippets/purchase-options sets sub = its plan data and calls setMode)
      this.mode = 'once';
      this.plan = '';
      this.sub = this.sub || null;
      this.renderOptions();
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-tier-n]');
        if (!input) return;
        this.chosen = { n: parseInt(input.dataset.tierN, 10), pct: parseFloat(input.dataset.tierPct) || 0 };
        this.apply();
      });
      this.preselectFromLink();
    }
    // Nothing is preselected (Р-117) — except when the shopper arrives from the home page's offer band «3 Pakete wählen»
    // (sections/offer-band links to ?packs=3#Tiers): that rung is chosen once the page is ready; the size is still asked.
    preselectFromLink() {
      let want = '';
      try {
        want = new URLSearchParams(window.location.search).get('packs') || '';
      } catch {
        return;
      }
      if (!/^[23]$/.test(want)) return;
      const go = () => {
        const input = this.querySelector(`[data-tier-n="${want}"]`);
        if (!input || input.disabled || this.chosen) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setTimeout(go, 0), { once: true });
      else setTimeout(go, 0);
    }
    bundle(n) {
      // the bundle variant of the chosen size for n packs; before a size is chosen, any size of that bundle (the price is one)
      const tier = this.bundles && n > 1 ? this.bundles[String(n)] : null;
      if (!tier) return null;
      return tier[this.size] || Object.values(tier)[0] || null;
    }
    tierTotal(n, pct) {
      const b = this.bundle(n);
      if (b) return b.price;
      const st = this.setPacks ? this.setTotal(n) : 0;
      if (st) return st;
      // Shopify's native percentage discount is rounded down per unit, then multiplied by the
      // quantity — verified on the store's own rules 30.09.2026 (11 carts, e.g. 3 × 24,90 € − 35 % = 48,57 €,
      // not 48,56 €). The page shows exactly what the cart will charge.
      const unitDiscount = Math.floor((this.base * pct) / 100 + 1e-9);
      return n * (this.base - unitDiscount);
    }
    // n sets as the cart settles them (packPlan, the same pack map): their packs in the cheapest split into sets, and
    // from map.min packs single packs at the shop's −map.pct %, rounded down per pack (3 × «4 Paar» = 6 × 12,00 € =
    // 72,00 €); a sold-out single pack keeps the sets. 0 when the map does not know the set.
    setTotal(n) {
      const map = packMap();
      const set = map && map.b ? map.b[String(this.single)] : null;
      const s = set && map.s ? map.s[String(set.s)] : null;
      if (!s) return 0;
      const t = n * set.n;
      if (map.pct > 0 && t >= map.min) return s.ok ? t * (s.price - Math.floor((s.price * map.pct) / 100 + 1e-9)) : n * this.base;
      return packSplit(t, s).cost;
    }
    isSub() {
      return this.mode === 'sub' && !!this.plan && !!this.sub;
    }
    // the plan price of one pack of a variant (selling_plan_allocation.price)
    planPrice(id, plan = this.plan) {
      const v = this.sub && this.sub.v ? this.sub.v[String(id)] : null;
      return v ? v[String(plan)] || 0 : 0;
    }
    // n packs on subscription: the single pack × n with the plan. With the subscription set prices switched on (the
    // automatic subscription discounts exist, block setting sub_set_prices) 2 and 3 packs cost the set price minus the
    // plan percent, per pack rounded down to the cent as the checkout does; otherwise n × the plan price of one pack
    // (dearer than the set, so snippets/purchase-options does not render the step on ladder products until then).
    subTotal(n, plan = this.plan) {
      const one = this.planPrice(this.single, plan);
      const pct = (this.sub && this.sub.pct) || 0;
      const b = n > 1 && this.sub && this.sub.set && pct > 0 ? this.bundle(n) : null;
      if (!b) return n * one;
      return n * Math.floor((b.price * (100 - pct)) / (100 * n) + 1e-9);
    }
    cartTotal(n, pct) {
      return this.isSub() ? this.subTotal(n) : this.tierTotal(n, pct);
    }
    setMode(mode, plan) {
      this.mode = mode;
      this.plan = plan || '';
      if (this.chosen) {
        this.apply();
        return;
      }
      const root = this.closest('[data-product-root]') || document;
      const field = (this.closest('form') || root).querySelector('[data-selling-plan]');
      if (field) {
        field.value = this.isSub() ? this.plan : '';
        field.disabled = !this.isSub();
      }
      syncCta(root);
    }
    setVariant(variant) {
      // a size was chosen: the bundles follow it, the rung prices start from its price
      this.size = (variant.options && variant.options[0]) || this.size;
      this.single = String(variant.id);
      this.sizeChosen = true;
      if (variant.price) this.base = variant.price;
      this.renderOptions();
      if (this.chosen) this.apply();
    }
    setBase(cents) {
      if (!cents || cents === this.base) return;
      this.base = cents;
      this.renderOptions();
      if (this.chosen) this.apply();
    }
    renderOptions() {
      this.querySelectorAll('[data-tier-n]').forEach((input) => {
        const n = parseInt(input.dataset.tierN, 10);
        let pct = parseFloat(input.dataset.tierPct) || 0;
        const body = input.nextElementSibling;
        if (!body) return;
        const total = this.tierTotal(n, pct);
        const full = n * this.base;
        const b = this.bundle(n);
        if (b) {
          pct = full > 0 ? Math.round(((full - total) * 100) / full) : 0;
          input.dataset.tierPct = String(pct);
          input.disabled = b.available === false;
          const badge = body.querySelector('[data-tier-badge]');
          if (badge) badge.textContent = `−${pct}\u00a0%`;
        }
        const priceEl = body.querySelector('[data-tier-price]');
        if (priceEl) priceEl.textContent = money(total, this.fmt);
        const wasEl = body.querySelector('[data-tier-was]');
        if (wasEl) {
          wasEl.hidden = pct <= 0;
          if (pct > 0) wasEl.textContent = money(full, this.fmt);
        }
        const perEl = body.querySelector('[data-tier-per]');
        if (perEl && this.pairs > 0 && this.perTemplate) perEl.textContent = this.perTemplate.replace('[price]', money(Math.round(total / (n * this.pairs)), this.fmt));
        const free = this.freeFrom > 0 && total >= this.freeFrom;
        const shipEl = body.querySelector('[data-tier-ship]');
        if (shipEl) shipEl.hidden = !free;
      });
    }
    apply() {
      const root = this.closest('[data-product-root]') || document;
      const { n, pct } = this.chosen;
      const form = this.closest('form') || root;
      const qty = form.querySelector('[data-tier-qty]');
      // on subscription always the single pack × n (no subscription on Shopify bundles)
      const sub = this.isSub();
      const b = sub ? null : this.bundle(n);
      // a bundle is one product with its own price: added once; a single pack — n times
      if (qty) qty.value = b ? 1 : n;
      if (this.bundles) {
        const idInput = form.querySelector('input[name="id"]');
        // only once a size is chosen: before that the form has no variant and the button asks for the size
        const sizeChosen = this.sizeChosen || (root.dataset ? root.dataset.sizeOk === '1' : false);
        if (idInput && sizeChosen) idInput.value = b && this.bundles[String(n)] && this.bundles[String(n)][this.size] ? this.bundles[String(n)][this.size].id : this.single;
      }
      // the plan goes to the cart only on subscription; an empty selling_plan never reaches cart/add.js
      const field = form.querySelector('[data-selling-plan]');
      if (field) {
        field.value = sub ? this.plan : '';
        field.disabled = !sub;
      }
      const total = this.cartTotal(n, pct);
      // struck: the same packs at the single price (the one rule of the ladder and the subscription); a subscription
      // that is not cheaper than buying the same packs once (2 and 3 packs before the subscription discounts) strikes
      // nothing and names no saving
      const deal = sub ? total < this.tierTotal(n, pct) : pct > 0;
      updatePriceLockup(root, total, deal ? n * this.base : 0, n);
      syncCta(root);
    }
  }
  customElements.define('tier-picker', TierPicker);

  // «Einmalkauf oder Abo?» (snippets/purchase-options, only on products with selling plans). With the pack ladder it
  // switches the tier picker's mode (the cart line, the prices, the button); without one (the cream) it sets the plan and
  // the price itself. The card shows the subscription price of the chosen pack count; the struck one-time price beside it.
  class PurchaseOptions extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('[data-selling-plans]')?.textContent || 'null');
      } catch {
        this.data = null;
      }
      if (!this.data) return;
      this.data.set = this.dataset.setPrices === '1';
      this.root = this.closest('[data-product-root]') || document;
      this.form = this.closest('form');
      this.tiers = this.root.querySelector('tier-picker');
      if (this.tiers) this.tiers.sub = this.data;
      this.addEventListener('change', () => this.update());
      // a size or a pack count chosen elsewhere in the form: the card follows (the form hears it after them)
      if (this.form) {
        this.form.addEventListener('change', (e) => {
          if (this.contains(e.target)) return;
          if (this.tiers) this.render();
          else this.update();
        });
      }
      this.update();
    }
    mode() {
      return this.querySelector('input[name="purchase_mode"]:checked')?.value || 'once';
    }
    planId() {
      const on = this.querySelector('input[name="purchase_plan"]:checked') || this.querySelector('input[name="purchase_plan"]');
      return on ? on.value : '';
    }
    variantId() {
      return (this.form || this.root).querySelector('input[name="id"]')?.value || '';
    }
    update() {
      const sub = this.mode() === 'sub';
      const plan = sub ? this.planId() : '';
      const more = this.querySelector('[data-sub-more]');
      if (more) more.hidden = !sub;
      // the cash-on-delivery and Klarna rows under the button hide while a subscription is chosen (purchase-options CSS)
      if (this.root.dataset) this.root.dataset.subOn = sub ? '1' : '';
      if (this.tiers && this.tiers.setMode) {
        this.tiers.setMode(sub ? 'sub' : 'once', plan);
      } else {
        const field = this.querySelector('[data-selling-plan]');
        const id = this.variantId();
        const once = this.data.p[id] || 0;
        const onPlan = (this.data.v[id] || {})[plan] || 0;
        if (field) {
          field.value = sub && onPlan ? plan : '';
          field.disabled = !(sub && onPlan);
        }
        if (once) updatePriceLockup(this.root, sub && onPlan ? onPlan : once, sub && onPlan ? once : 0);
      }
      this.render();
    }
    render() {
      const t = this.tiers;
      const plan = this.planId();
      const fmt = t ? t.fmt : this.root.querySelector('[data-price]')?.dataset.moneyFormat;
      let now = 0;
      let was = 0;
      let from = false;
      if (t && t.chosen) {
        now = t.subTotal(t.chosen.n, plan);
        was = t.tierTotal(t.chosen.n, t.chosen.pct);
      } else if (t) {
        now = t.planPrice(t.single, plan);
        was = t.base;
        from = true;
      } else {
        const id = this.variantId() || Object.keys(this.data.p)[0];
        now = (this.data.v[id] || {})[plan] || 0;
        was = this.data.p[id] || 0;
      }
      const priceEl = this.querySelector('[data-sub-price]');
      if (priceEl) priceEl.textContent = now ? money(now, fmt) : '';
      // struck price and chip only while the subscription is cheaper than buying the same packs once
      const cheaper = !!now && was > now;
      const wasEl = this.querySelector('[data-sub-was]');
      if (wasEl) wasEl.textContent = cheaper ? money(was, fmt) : '';
      const chip = this.querySelector('.tier__pct');
      if (chip) chip.hidden = !cheaper;
      const fromEl = this.querySelector('[data-sub-from]');
      if (fromEl) fromEl.hidden = !from;
    }
  }
  customElements.define('purchase-options', PurchaseOptions);

  class QtyInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((b) => {
        b.addEventListener('click', (e) => {
          e.preventDefault();
          const step = b.dataset.step === 'down' ? -1 : 1;
          const min = parseInt(this.input.min || '1', 10);
          this.input.value = Math.max(min, (parseInt(this.input.value, 10) || min) + step);
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }
  customElements.define('qty-input', QtyInput);

  const showCartError = (root, message) => {
    const el = document.createElement('span');
    el.textContent = typeof message === 'string' && message.trim() ? message : root.dataset.errorMessage;
    toast(el.outerHTML);
  };

  // Only whole decimal pack counts: invalid input restores the confirmed quantity, never removes a line.
  const typedQuantity = (input) => {
    const raw = String(input.value || '').trim();
    const n = Number(raw);
    if (!/^\d+$/.test(raw) || !Number.isSafeInteger(n) || input.validity?.valid === false) {
      input.value = input.defaultValue;
      return null;
    }
    return n;
  };

  const recoverCart = async (root, sectionId) => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sectionId);
      const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error();
      const sections = await res.json();
      if (!root.refresh(sections[sectionId])) throw new Error();
      await updateCartCount();
      return true;
    } catch {
      root.querySelectorAll('[data-drawer-qty], [data-line-qty]').forEach((input) => {
        input.value = input.defaultValue;
      });
      return false;
    }
  };

  // Before a cart update the lines are rebuilt from the server: remember which control of which line had the
  // keyboard, put it back on the same control of the new line (or of the line that took the removed one's place,
  // or on the empty cart's action), and say the new total in a quiet live region (WCAG 2.4.3, 4.1.3).
  const focusSpot = (root) => {
    const el = document.activeElement;
    if (!el || !root.contains(el)) return null;
    const keyed = el.matches('[data-key]') ? el : el.closest('qty-input')?.querySelector('[data-key]');
    const role = el.dataset.step || (el.matches('input') ? 'qty' : el.matches('[data-drawer-remove], [data-line-remove]') ? 'remove' : null);
    if (!keyed || !role) return null;
    return { key: keyed.dataset.key, line: parseInt(keyed.dataset.line, 10) || 1, role };
  };
  const restoreFocus = (root, spot) => {
    if (!spot) return;
    const pick = (line) => (spot.role === 'remove' ? line.querySelector('[data-drawer-remove], [data-line-remove]')
      : spot.role === 'qty' ? line.querySelector('input[data-key]') : line.querySelector(`[data-step="${spot.role}"]`));
    const lines = [...root.querySelectorAll('[data-cart-line]')];
    const same = lines.find((line) => [...line.querySelectorAll('[data-key]')].some((el) => el.dataset.key === spot.key));
    const line = same || lines[Math.min(spot.line, lines.length) - 1];
    const target = line ? pick(line) : root.querySelector('[data-cart-empty-focus]');
    target?.focus({ preventScroll: true });
  };
  const announceCart = (root) => {
    const status = root.querySelector('[data-cart-status]') || document.querySelector('[data-cart-status]');
    if (!status) return;
    const total = root.querySelector('[data-cart-total]');
    // the cart's one hint (snippets/ladder-hint): «Noch 0,10 € bis zum Gratisversand. Mit 4 weiteren Paketen …»
    const ship = root.querySelector('[data-cart-hint-text]');
    status.textContent = [total?.textContent, ship?.textContent].map((t) => (t || '').replace(/\s+/g, ' ').trim()).filter(Boolean).join(' · ');
  };

  const changeCart = async (root, key, quantity, sectionId) => {
    if (root.busy || !key) return;
    root.busy = true;
    root.setAttribute('aria-busy', 'true');
    const spot = focusSpot(root);
    const area = root.querySelector('[data-drawer-swap]') || root;
    const controls = [...area.querySelectorAll('button, input, textarea')].filter((el) => !el.disabled);
    controls.forEach((el) => { el.disabled = true; });
    return cartQueue().run(async () => {
      // An app may have changed discount-derived keys while this action waited.
      if (![...root.querySelectorAll('[data-drawer-qty], [data-line-qty], [data-drawer-remove], [data-line-remove]')].some((el) => el.dataset.key === key)) {
        showCartError(root);
        await recoverCart(root, sectionId);
        controls.forEach((el) => { el.disabled = false; });
        root.removeAttribute('aria-busy');
        root.busy = false;
        return;
      }
      const event = cartQueue().begin(root, 'CartLinesUpdateEvent', {
        action: quantity === 0 ? 'remove' : 'update',
        context: sectionId === 'cart-drawer' ? 'dialog' : 'cart',
        lines: [{ id: key, quantity }],
    });
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: [sectionId] }),
      });
      const data = await res.json();
      if (!res.ok) {
        showCartError(root, data.description || data.message);
        await recoverCart(root, sectionId);
        event.resolve(await updateCartCount(), [{ code: 'INVALID', message: data.description || data.message || root.dataset.errorMessage }]);
        return;
      }
      const view = await settlePacks([sectionId]) || data;
      if (!root.refresh(view.sections?.[sectionId]) && !await recoverCart(root, sectionId)) {
        showCartError(root);
      }
      syncCartCount(view);
      event.resolve(view);
      restoreFocus(root, spot);
      announceCart(root);
    } catch (error) {
      event.reject(error);
      showCartError(root);
      await recoverCart(root, sectionId);
    } finally {
      controls.forEach((el) => { el.disabled = false; });
      root.removeAttribute('aria-busy');
      root.busy = false;
    }
    });
  };

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.submit(e));
    }
    async submit(e) {
      e.preventDefault();
      // one add per press: a second tap while the first is on its way does nothing (it used to put the packs in twice)
      if (this.busy) return;
      const button = this.form.querySelector('[data-add-button]');
      const root = this.closest('[data-product-root]') || document;
      if (button && button.getAttribute('aria-disabled') === 'true') {
        showNeed(root);
        return;
      }
      const drawer = document.querySelector('cart-drawer');
      this.busy = true;
      if (button) {
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
        if (button.dataset.textAdding) button.textContent = button.dataset.textAdding;
      }
      return cartQueue().run(async () => {
        let event;
        try {
          const body = new FormData(this.form);
          event = cartQueue().begin(this, 'CartLinesUpdateEvent', {
            action: 'add', context: 'product',
            lines: [{ merchandiseId: body.get('id'), quantity: parseInt(body.get('quantity') || '1', 10) || 1 }],
          });
          if (drawer) body.append('sections', 'cart-drawer');
          const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body,
          });
          const data = await res.json();
          if (!res.ok) {
            showCartError(this, data.description || data.message);
            event.resolve(await updateCartCount(), [{ code: 'INVALID', message: data.description || data.message || this.dataset.errorMessage }]);
          } else {
            const settled = await settlePacks(drawer ? ['cart-drawer'] : undefined);
            const cart = settled || await updateCartCount();
            if (settled) syncCartCount(settled);
            event.resolve(cart);
            // the pack is in the cart now: the sticky bar says «Zur Kasse» until a new choice (syncCta in finally)
            if (root.dataset) {
              root.dataset.inCart = '1';
              root.dataset.choiceFresh = '';
            }
            if (drawer) {
              if (!drawer.refresh((settled || data).sections?.['cart-drawer']) && !await recoverCart(drawer, 'cart-drawer')) {
                window.location.href = this.dataset.cartUrl || '/cart';
                return;
              }
              drawer.open();
            } else {
              const msg = this.dataset.successMessage || 'Added to cart.';
              const label = this.dataset.cartLabel || 'Cart';
              const cartUrl = this.dataset.cartUrl || '/cart';
              toast(`<span>${msg}</span><a href="${cartUrl}">${label} →</a>`);
            }
          }
        } catch (error) {
          event?.reject(error);
          window.location.href = this.dataset.cartUrl || '/cart';
        } finally {
          this.busy = false;
          if (button) {
            button.removeAttribute('aria-busy');
            button.disabled = false;
          }
          syncCta(root);
        }
      });
    }
  }
  customElements.define('product-form', ProductForm);

  // BoxBuilder — «build your own pack» (sections/box-builder): + and − per product for the chosen size, the summary
  // with every product × size, the price with −ladder_tier6_pct % from 6 packs (rounded down per pack, as the
  // checkout does), one click puts it all in the cart drawer. The prices move with the box (owner's words 15.10.2026:
  // show through the design that it is dynamic): every row says what the pack costs from 6 packs and, from 6, shows
  // that price in green over the struck regular one; the summary strikes the regular sum of a line when sets or the
  // −% make it cheaper, shows the price per pair; six slots fill with the packs (or the bar, settings.progress_style);
  // an empty box shows the price per pack at 1 · 2 · 3 · 6 packs instead of «0,00 €»; on a phone a line with the total
  // and the button stays at the bottom while the summary is below the screen. Each size chip counts its packs.
  class BoxBuilder extends HTMLElement {
    connectedCallback() {
      try {
        this.products = JSON.parse(this.dataset.products || '[]');
      } catch {
        this.products = [];
      }
      // the lines stay together: the 4-pair packs (Klassik) first, then the 2-pair ones (In Farben)
      this.products = this.products.map((p, i) => ({ ...p, i })).sort((a, b) => (b.pairs - a.pairs) || (a.i - b.i));
      this.six = parseInt(this.dataset.six, 10) || 0;
      this.threshold = parseInt(this.dataset.threshold, 10) || 0;
      this.fmt = this.dataset.moneyFormat;
      this.box = {};
      this.n = 0;
      const pressed = this.querySelector('.bbox__size[aria-pressed="true"]') || this.querySelector('.bbox__size');
      this.size = pressed ? pressed.dataset.size : '';
      this.querySelectorAll('.bbox__size').forEach((b) => b.addEventListener('click', () => {
        this.size = b.dataset.size;
        this.querySelectorAll('.bbox__size').forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
        this.render();
      }));
      this.querySelector('[data-rows]').addEventListener('click', (e) => {
        const btn = e.target.closest('[data-step]');
        if (!btn || btn.disabled) return;
        const key = btn.dataset.key;
        const step = btn.dataset.step;
        this.box[key] = Math.max(0, (this.box[key] || 0) + (step === 'up' ? 1 : -1));
        if (!this.box[key]) delete this.box[key];
        this.render();
        // the rows are rebuilt: the keyboard goes back to the pressed button (or to + when − is now off)
        const again = this.querySelector(`[data-key="${key}"][data-step="${step}"]`);
        const target = again && !again.disabled ? again : this.querySelector(`[data-key="${key}"][data-step="up"]`);
        target?.focus({ preventScroll: true });
      });
      this.querySelector('[data-add]').addEventListener('click', () => this.add());
      this.dock = this.querySelector('[data-dock]');
      this.dock?.querySelector('[data-dock-add]')?.addEventListener('click', () => this.add());
      const sum = this.querySelector('.bbox__sum');
      this.sumBelow = false;
      if (this.dock && sum && 'IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          this.sumBelow = !entry.isIntersecting && entry.boundingClientRect.top > 0;
          this.syncDock();
        }).observe(sum);
      }
      this.render();
    }
    lines() {
      return Object.entries(this.box).map(([key, qty]) => {
        const [pid, size] = key.split('|');
        const p = this.products.find((x) => String(x.id) === pid);
        return { key, qty, size, p, variant: p && p.variants[size] };
      }).filter((l) => l.p && l.variant);
    }
    unit(price, n) {
      return n >= 6 && this.six > 0 ? price - Math.floor((price * this.six) / 100 + 1e-9) : price;
    }
    // what each summary line costs in the cart, by key: from 6 packs single packs at −six %; below that identical packs
    // as the shop's sets (2, 3, 2 + 2, 3 + 2 — the cart makes the same swap, settlePacks). With the shop's line discounts
    // (map.mix) a whole line — Klassik, In Farben, sizes and colours mixed — costs its sets for its number of packs; the
    // saving is spread over the line's rows by their regular price, as the checkout does.
    costs(lines, n) {
      const map = packMap();
      const out = {};
      const groups = {};
      lines.forEach((l) => {
        const s = map?.s[String(l.variant.id)];
        if (n >= 6 || !s) out[l.key] = l.qty * this.unit(l.p.price, n);
        else if (!map.mix) out[l.key] = packSplit(l.qty, s).cost;
        else (groups[s.l || l.key] = groups[s.l || l.key] || []).push({ l, s });
      });
      Object.values(groups).forEach((group) => {
        const s = group[0].s;
        const packs = group.reduce((sum, g) => sum + g.l.qty, 0);
        const full = group.reduce((sum, g) => sum + g.l.qty * g.s.price, 0);
        // the line discount needs no set in stock: it prices the single packs
        const sets = { price: s.price, 2: s['2'] && { ...s['2'], ok: true }, 3: s['3'] && { ...s['3'], ok: true } };
        const saving = full - packSplit(packs, sets).cost;
        let left = saving;
        group.forEach((g, i) => {
          const share = i === group.length - 1 ? left : Math.round((saving * g.l.qty * g.s.price) / full);
          left -= share;
          out[g.l.key] = g.l.qty * g.s.price - share;
        });
      });
      return out;
    }
    dotsHtml(p) {
      return (p.dots || '').split(',').filter(Boolean).map((c) => `<span class="dot" style="background:${c.trim()}"></span>`).join('');
    }
    // the empty box: what one pack costs at 1, 2, 3 and 6 packs, per line (the first product of each line, the chosen size)
    ladderTable() {
      const t = this.dataset;
      const map = packMap();
      const groups = [];
      this.products.forEach((p) => {
        const name = String(p.title).split(' — ')[0];
        if (!groups.some((g) => g.name === name)) groups.push({ name, p });
      });
      const rows = groups.map(({ name, p }) => {
        const v = p.variants[this.size] || Object.values(p.variants || {})[0];
        const s = v && map ? map.s[String(v.id)] : null;
        const per = (k) => (s && s[k] ? Math.round(s[k].price / Number(k)) : 0);
        const cells = [p.price, per('2'), per('3'), this.six > 0 ? this.unit(p.price, 6) : 0];
        return `<tr><th scope="row">${name}</th>${cells.map((c, i) => `<td${i === 3 ? ' class="is-six"' : ''}>${c ? money(c, this.fmt) : '—'}</td>`).join('')}</tr>`;
      }).join('');
      return `<p class="bbox__ladder-title">${t.tLadderTitle}</p>`
        + `<table class="bbox__ladder-t"><thead><tr><th scope="col">${t.tLadderPacks}</th><th scope="col">1</th><th scope="col">2</th><th scope="col">3</th><th scope="col">${t.tLadderSix}</th></tr></thead><tbody>${rows}</tbody></table>`
        + `<p class="bbox__ladder-note">${t.tLadderNote}</p>`;
    }
    render() {
      const t = this.dataset;
      const lines = this.lines();
      const n = lines.reduce((s, l) => s + l.qty, 0);
      const six = n >= 6 && this.six > 0;
      const crossed = this.prevN !== undefined && this.prevN < 6 && six;
      this.prevN = n;
      this.n = n;
      const rows = this.querySelector('[data-rows]');
      // a product without the chosen size is not listed for it (Klassik Schwarz has no 35–38): no greyed-out + without a word
      rows.innerHTML = this.products.filter((p) => p.variants[this.size]).map((p) => {
        const v = p.variants[this.size];
        const key = `${p.id}|${this.size}`;
        const q = this.box[key] || 0;
        const price = money(p.price, this.fmt);
        const low = this.unit(p.price, 6);
        // «4 para» / «4 pari»: the label comes per product with the real count (Croatian and Slovene plural forms)
        const pairs = p.pairsLabel || t.tPairs.replace('[n]', p.pairs);
        const meta = six && low < p.price ? `${pairs} · <s>${price}</s>` : `${pairs} · ${price}`;
        const off = !v || v.available === false;
        // a sold-out size says so where «ab 6 Paketen» stands
        const then = off && t.tSoldOut ? `<span class="bbox__then">${t.tSoldOut}</span>`
          : this.six > 0 && low < p.price ? `<span class="bbox__then${six ? ' is-on' : ''}">${t.tFromSix.replace('[price]', money(low, this.fmt))}</span>` : '';
        return `<div class="bbox__row"><span class="dots" aria-hidden="true">${this.dotsHtml(p)}</span><span class="bbox__info"><span class="bbox__name">${p.title}</span><span class="bbox__meta">${meta}</span>${then}</span>`
          + `<span class="bbox__ctrl"><button type="button" class="bbox__btn" data-step="down" data-key="${key}" aria-label="−" ${q ? '' : 'disabled'}>−</button>`
          + `<span class="bbox__n">${q}</span><button type="button" class="bbox__btn bbox__btn--plus" data-step="up" data-key="${key}" aria-label="+" ${off ? 'disabled' : ''}>+</button></span></div>`;
      }).join('');
      // each size chip: how many packs of that size are in the box
      this.querySelectorAll('.bbox__size').forEach((chip) => {
        const c = lines.filter((l) => l.size === chip.dataset.size).reduce((s, l) => s + l.qty, 0);
        const badge = chip.querySelector('[data-size-n]');
        if (badge) {
          badge.textContent = c;
          badge.hidden = !c;
        }
        const sr = chip.querySelector('[data-size-sr]');
        if (sr) sr.textContent = c ? `, ${(t.tSizeIn || '').replace('[count]', c)}` : '';
      });
      const pairs = lines.reduce((s, l) => s + l.qty * (l.p.pairs || 0), 0);
      const raw = lines.reduce((s, l) => s + l.qty * l.p.price, 0);
      const costs = this.costs(lines, n);
      const total = lines.reduce((s, l) => s + costs[l.key], 0);
      this.querySelector('[data-count]').textContent = n ? t.tCount.replace('[packs]', n).replace('[pairs]', pairs) : t.tEmpty;
      // progress, look A: six slots that fill with the packs (their colour dots); green at 6; «+N» past 6
      const slots = this.querySelector('[data-slots]');
      if (slots) {
        const filled = [];
        lines.forEach((l) => { for (let i = 0; i < l.qty; i += 1) filled.push(l); });
        slots.classList.toggle('is-max', n >= 6);
        [...slots.children].forEach((li, i) => {
          const l = filled[i];
          li.className = l ? 'is-on' : '';
          li.innerHTML = i === 5 && n > 6 ? `<span class="bbox__plus">+${n - 5}</span>` : (l ? `<span class="dots">${this.dotsHtml(l.p)}</span>` : '');
        });
        const cap = this.querySelector('[data-slots-n]');
        if (cap) {
          cap.textContent = six ? t.tDone : t.tSlots.replace('[n]', n);
          cap.classList.toggle('is-done', six);
        }
        const goal = this.querySelector('[data-slots-goal]');
        if (goal) goal.hidden = six;
      }
      // progress, look B: the bar 0 … 6
      const fill = this.querySelector('[data-fill]');
      if (fill) {
        fill.style.width = `${Math.min((n / 6) * 100, 100)}%`;
        fill.classList.toggle('is-max', n >= 6);
      }
      const ladder = this.querySelector('[data-ladder]');
      if (ladder) {
        ladder.hidden = n > 0;
        if (!n) ladder.innerHTML = this.ladderTable();
      }
      this.querySelector('[data-list]').innerHTML = lines.map((l) => {
        const c = costs[l.key];
        const full = l.qty * l.p.price;
        // the size never splits ("39–" / "42"): « · 39–42» wraps as one piece; in a narrow summary it is its own line (CSS)
        return `<li><span class="bbox__ln">${l.qty}&nbsp;×&nbsp;${l.p.title} <span class="bbox__lsz"><span class="bbox__lsep">·&nbsp;</span>${l.size.replace('-', '–')}</span></span><span class="bbox__lp">${c < full ? `<s>${money(full, this.fmt)}</s> ` : ''}<span>${money(c, this.fmt)}</span></span></li>`;
      }).join('');
      const hint = this.querySelector('[data-hint]');
      const missing = 6 - n;
      hint.classList.toggle('is-done', n >= 6);
      hint.textContent = n >= 6 ? t.tDone : (missing === 1 ? t.tMoreOne : t.tMore.replace('[count]', missing));
      const totalRow = this.querySelector('[data-total-row]');
      if (totalRow) totalRow.hidden = !n;
      const was = this.querySelector('[data-was]');
      was.hidden = total >= raw;
      was.textContent = money(raw, this.fmt);
      this.querySelector('[data-now]').textContent = money(total, this.fmt);
      const save = this.querySelector('[data-save]');
      save.hidden = total >= raw;
      save.textContent = t.tSave.replace('[amount]', money(raw - total, this.fmt));
      const pair = this.querySelector('[data-pair]');
      if (pair) {
        pair.hidden = !n || !pairs;
        if (n && pairs) {
          const now = money(Math.round(total / pairs), this.fmt);
          pair.textContent = total < raw ? t.tPerPair.replace('[now]', now).replace('[was]', money(Math.round(raw / pairs), this.fmt)) : t.tPerPairPlain.replace('[now]', now);
        }
      }
      const ship = this.querySelector('[data-ship]');
      const free = this.threshold > 0 && total >= this.threshold;
      ship.classList.toggle('is-ok', free);
      ship.textContent = !n || !this.threshold ? '' : (free ? t.tShipOk : t.tShip.replace('[amount]', money(this.threshold - total, this.fmt)));
      const add = this.querySelector('[data-add]');
      add.disabled = n === 0;
      add.textContent = n ? t.tAdd.replace('[price]', money(total, this.fmt)) : t.tFirst;
      // reaching 6: the rows and the total light up once (nothing with reduced motion, base CSS)
      if (crossed) {
        [...rows.children, this.querySelector('.bbox__total')].forEach((el) => el && el.classList.add('is-flash'));
        clearTimeout(this.flashTimer);
        this.flashTimer = setTimeout(() => this.querySelectorAll('.is-flash').forEach((el) => el.classList.remove('is-flash')), 900);
      }
      if (this.dock) {
        const dt = this.dock.querySelector('[data-dock-total]');
        if (dt) dt.textContent = money(total, this.fmt);
        const dh = this.dock.querySelector('[data-dock-hint]');
        if (dh) {
          dh.textContent = six ? t.tDone : t.tBarMore.replace('[count]', missing);
          dh.classList.toggle('is-done', six);
        }
        this.syncDock();
      }
    }
    // the phone line: only with packs in the box and while the summary is below the screen; out of the Tab order when hidden
    syncDock() {
      if (!this.dock) return;
      const show = this.n > 0 && this.sumBelow;
      this.dock.classList.toggle('is-visible', show);
      this.dock.inert = !show;
      const btn = this.dock.querySelector('[data-dock-add]');
      if (btn) btn.disabled = !this.n;
    }
    async add() {
      const lines = this.lines();
      if (!lines.length) return;
      const add = this.querySelector('[data-add]');
      const dockAdd = this.dock?.querySelector('[data-dock-add]');
      add.disabled = true;
      add.setAttribute('aria-busy', 'true');
      if (dockAdd) dockAdd.disabled = true;
      const drawer = document.querySelector('cart-drawer');
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lines.map((l) => ({ id: l.variant.id, quantity: l.qty })), sections: drawer ? 'cart-drawer' : undefined }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || '');
        const settled = await settlePacks(drawer ? ['cart-drawer'] : undefined);
        if (settled) syncCartCount(settled);
        else await updateCartCount();
        if (drawer && drawer.refresh((settled || data).sections?.['cart-drawer'])) {
          drawer.open();
        } else {
          window.location.href = this.dataset.cartUrl || '/cart';
        }
        this.box = {};
        this.render();
      } catch (error) {
        toast(`<span>${(error && error.message) || this.dataset.tError}</span>`);
      } finally {
        add.removeAttribute('aria-busy');
        add.disabled = !this.lines().length;
        this.syncDock();
      }
    }
  }
  customElements.define('box-builder', BoxBuilder);

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target.closest('[data-drawer-close-link]')) {
          this.close();
          return;
        }
        const remove = e.target.closest('[data-drawer-remove]');
        if (remove) this.change(remove.dataset.key, 0);
      });
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-drawer-qty]');
        const n = input ? typedQuantity(input) : null;
        if (n !== null) this.change(input.dataset.key, n);
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }
    open() {
      if (!this.classList.contains('is-open')) this.opener = document.activeElement;
      this.classList.add('is-open');
      document.body.classList.add('drawer-open');
      this.outsideInert(true);
      this.querySelector('button[data-drawer-close]')?.focus();
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
      this.outsideInert(false);
      const back = this.opener && document.contains(this.opener) && this.opener !== document.body ? this.opener : document.querySelector('[data-cart-link]');
      back?.focus({ preventScroll: true });
      this.opener = null;
    }
    // aria-modal alone does not stop Tab: everything beside the drawer is made inert while it is open (perf.md, row 9);
    // the toast region stays live so «added» is still announced
    outsideInert(on) {
      Array.from(document.body.children).forEach((el) => {
        if (el.contains(this) || el.matches('.toast-region, script, style, link')) return;
        if (on && !el.inert) {
          el.inert = true;
          el.dataset.drawerInert = '';
        } else if (!on && el.hasAttribute('data-drawer-inert')) {
          el.inert = false;
          el.removeAttribute('data-drawer-inert');
        }
      });
    }
    refresh(html) {
      if (!html) return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-drawer-swap]');
      const swap = this.querySelector('[data-drawer-swap]');
      if (!fresh || !swap) return false;
      swap.innerHTML = fresh.innerHTML;
      // «2 Pakete» beside the title: a translated plural, rendered by the section with the lines
      const freshCount = doc.querySelector('[data-drawer-count]');
      const count = this.querySelector('[data-drawer-count]');
      if (freshCount && count) {
        count.textContent = freshCount.textContent;
        count.hidden = freshCount.hidden;
      }
      return true;
    }
    change(key, quantity) {
      return changeCart(this, key, quantity, 'cart-drawer');
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-cart-link]');
    if (!link) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || document.body.classList.contains('template-cart')) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    drawer.open();
  });

  // The cart note («A gift? Message for the card», drawer and cart page) is saved when it is left, so the next
  // re-render of the lines keeps the text; the checkout form also posts it.
  document.addEventListener('change', (e) => {
    const note = e.target.closest('[data-cart-note]');
    if (!note) return;
    const value = note.value;
    cartQueue().run(async () => {
      const root = note.closest('cart-drawer, cart-items') || note;
      const event = cartQueue().begin(root, 'CartNoteUpdateEvent', {
        context: root.matches('cart-drawer') ? 'dialog' : 'cart', note: value,
      });
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: value }),
        });
        const cart = await response.json();
        if (!response.ok) {
          showCartError(root, cart.description || cart.message);
          event.resolve(await updateCartCount(), [{ code: 'INVALID', message: cart.description || cart.message || root.dataset.errorMessage }]);
          return;
        }
        syncCartCount(cart);
        event.resolve(cart);
        document.querySelectorAll('[data-cart-note]').forEach((field) => {
          // Do not replace a newer edit waiting in the same queue.
          if (field.value === field.defaultValue || field === note && field.value === value) {
            field.value = field.defaultValue = cart.note || '';
          }
        });
      } catch (error) {
        showCartError(root);
        event.reject(error);
      }
    }).catch(() => {});
  });

  class CartItems extends HTMLElement {
    connectedCallback() {
      // a cart filled before (or elsewhere) is brought to the set prices once the cart page opens
      if (packMap()) cartQueue().run(async () => {
        const settled = await settlePacks([this.dataset.sectionId]);
        if (!settled) return;
        syncCartCount(settled);
        if (!this.refresh(settled.sections?.[this.dataset.sectionId])) window.location.reload();
      }).catch(() => {});
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-line-qty]');
        const n = input ? typedQuantity(input) : null;
        if (n !== null) this.change(input.dataset.key, n);
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-line-remove]');
        if (remove) {
          e.preventDefault();
          this.change(remove.dataset.key, 0);
        }
      });
    }
    refresh(html) {
      if (!html) return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('cart-items');
      if (!fresh) return false;
      this.innerHTML = fresh.innerHTML;
      return true;
    }
    change(key, quantity) {
      return changeCart(this, key, quantity, this.dataset.sectionId);
    }
  }
  customElements.define('cart-items', CartItems);

  // CartCode — the discount code field of the cart page and the drawer (snippets/cart-code). The cart itself checks a
  // code: cart/update.js gets the full list of codes (it replaces the old list), a code it answers with
  // applicable:false is taken out again at once, so a dead code never travels to the checkout. The section comes back
  // re-rendered with the new rows; the fresh field shows the message (kept here by section id, since the element is
  // replaced). × sends the list without that code. A cart whose sections cannot name a code (data-unknown) reads the
  // codes from cart.js for the chips.
  const codeNotes = {};
  const codeLists = {};
  class CartCode extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-code-form]');
      this.input = this.querySelector('input[name="code"]');
      this.msg = this.querySelector('[data-code-msg]');
      this.form?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.apply();
      });
      this.addEventListener('click', (e) => {
        const x = e.target.closest('[data-code-remove]');
        if (x) this.send(this.codes().filter((c) => c !== x.dataset.codeRemove), null);
      });
      const id = this.dataset.sectionId;
      if (!this.codes().length && (parseInt(this.dataset.unknown, 10) || 0) > 0) {
        if (codeLists[id]) this.chips(codeLists[id]);
        else this.loadCodes();
      }
      const note = codeNotes[id];
      if (note) {
        delete codeNotes[id];
        this.say(note);
      }
    }
    codes() {
      return [...this.querySelectorAll('[data-code-remove]')].map((b) => b.dataset.codeRemove);
    }
    say(note) {
      this.querySelector('details')?.setAttribute('open', '');
      // the keyboard stays where it was: on the field after a refused code or a removed one, on the line otherwise
      if (note.focus === 'input') this.input?.focus({ preventScroll: true });
      else if (note.focus) this.querySelector('summary')?.focus({ preventScroll: true });
      if (note.value && this.input) {
        this.input.value = note.value;
        this.input.select();
      }
      if (!this.msg) return;
      this.msg.classList.toggle('is-error', !!note.error);
      // a moment after the swap, so the fresh live region is read out
      setTimeout(() => { this.msg.textContent = note.text || ''; }, 60);
    }
    chips(list) {
      const box = this.querySelector('[data-code-chips]');
      if (!box) return;
      box.textContent = '';
      list.forEach((code) => {
        const chip = document.createElement('span');
        chip.className = 'badge badge--outline badge--pill ccode__chip';
        chip.append(code);
        const x = document.createElement('button');
        x.type = 'button';
        x.className = 'ccode__x';
        x.dataset.codeRemove = code;
        x.setAttribute('aria-label', (this.dataset.tRemove || '').replace('[code]', code));
        x.textContent = '×';
        chip.append(x);
        box.append(chip);
      });
      if (list.length) this.querySelector('details')?.setAttribute('open', '');
    }
    async loadCodes() {
      try {
        const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
        const list = (cart.discount_codes || []).filter((c) => c.applicable !== false).map((c) => c.code);
        codeLists[this.dataset.sectionId] = list;
        this.chips(list);
      } catch { /* the chips stay empty; the rows still show the saving */ }
    }
    apply() {
      const code = (this.input?.value || '').trim().toUpperCase();
      if (!code) {
        this.input?.focus();
        return null;
      }
      const next = [...new Set([...this.codes(), code])];
      return this.send(next, code);
    }
    send(next, added) {
      if (this.busy) return null;
      this.busy = true;
      const root = this.closest('cart-drawer, cart-items');
      const id = this.dataset.sectionId;
      const base = window.Shopify?.routes?.root || '/';
      root?.setAttribute('aria-busy', 'true');
      return cartQueue().run(async () => {
        const event = cartQueue().begin(root || this, 'CartDiscountUpdateEvent', { discountCodes: next.map((code) => ({ code })) });
        const post = async (codes) => {
          const res = await fetch(`${base}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: codes.join(','), sections: [id] }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.description || data.message || '');
          return data;
        };
        try {
          let cart = await post(next);
          const listed = cart.discount_codes || [];
          const ok = listed.filter((c) => c.applicable !== false).map((c) => c.code);
          if (ok.length !== listed.length) cart = await post(ok);
          const up = (c) => String(c).toUpperCase();
          const isBad = !!added && !ok.some((c) => up(c) === up(added));
          codeLists[id] = (cart.discount_codes || []).filter((c) => c.applicable !== false).map((c) => c.code);
          codeNotes[id] = added
            ? { text: (isBad ? this.dataset.tInvalid : this.dataset.tApplied).replace('[code]', added), error: isBad, focus: isBad ? 'input' : 'summary', value: isBad ? added : '' }
            : { text: '', focus: 'input' };
          if (!root || !root.refresh(cart.sections?.[id])) {
            if (!root || !await recoverCart(root, id)) showCartError(root || this);
          }
          syncCartCount(cart);
          event.resolve(cart);
          if (root) announceCart(root);
        } catch (error) {
          event.reject(error);
          showCartError(root || this, error && error.message);
        } finally {
          root?.removeAttribute('aria-busy');
          this.busy = false;
        }
      });
    }
  }
  customElements.define('cart-code', CartCode);

  class ProductGallery extends HTMLElement {
    // The gallery is one story: a track of frames that a phone swipes and a computer steps through with the
    // arrows, the thumbnails or the arrow keys. The bars and the thumbnails follow whichever frame is in view.
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.frames = Array.from(this.querySelectorAll('[data-gallery-frame]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.bars = Array.from(this.querySelectorAll('.pdp__bars i'));
      this.prev = this.querySelector('[data-gallery-prev]');
      this.next = this.querySelector('[data-gallery-next]');
      this.dialog = document.querySelector('[data-gallery-zoom]');
      this.still = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      if (this.frames.length > 1) {
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.mark(this.frames.indexOf(entry.target));
          });
        }, { root: this.track, threshold: 0.6 });
        this.frames.forEach((frame) => this.io.observe(frame));
        this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.go(i)));
        this.prev?.addEventListener('click', () => this.go(this.index - 1));
        this.next?.addEventListener('click', () => this.go(this.index + 1));
        this.track.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            this.go(this.index + (e.key === 'ArrowRight' ? 1 : -1), true);
          }
        });
      }
      this.frames.forEach((frame, i) => frame.addEventListener('click', () => this.zoom(i)));
      this.strip = this.querySelector('[data-gallery-thumbs]');
      // A mouse drags the photos and the thumbnails like a finger does; a trackpad scrolls them sideways.
      this.drag(this.track, (moved) => {
        const width = this.track.clientWidth || 1;
        const step = moved < 0 ? Math.ceil(this.track.scrollLeft / width - 0.15) : Math.floor(this.track.scrollLeft / width + 0.15);
        this.go(step);
      });
      if (this.strip) {
        this.drag(this.strip);
        // the vertical wheel scrolls the thumbnail strip while it can, then the page scrolls on as usual
        this.strip.addEventListener('wheel', (e) => {
          if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
          const max = this.strip.scrollWidth - this.strip.clientWidth;
          if (max <= 0) return;
          if ((e.deltaY < 0 && this.strip.scrollLeft <= 0) || (e.deltaY > 0 && this.strip.scrollLeft >= max - 1)) return;
          e.preventDefault();
          this.strip.scrollLeft += e.deltaY;
        }, { passive: false });
        const edges = () => {
          const max = this.strip.scrollWidth - this.strip.clientWidth;
          this.strip.classList.toggle('can-left', this.strip.scrollLeft > 2);
          this.strip.classList.toggle('can-right', this.strip.scrollLeft < max - 2);
        };
        this.strip.addEventListener('scroll', edges, { passive: true });
        window.addEventListener('resize', edges);
        edges();
      }
      this.setupZoom();
      // The other frames and the thumbnails get their source after the cover: once the cover has arrived, or at the
      // first touch — never while the cover is still on its way (perf.md, row 7).
      const wake = () => this.hydrate();
      const cover = this.frames[0]?.querySelector('img');
      if (!cover || cover.complete) setTimeout(wake, 300);
      else {
        cover.addEventListener('load', () => setTimeout(wake, 300), { once: true });
        cover.addEventListener('error', wake, { once: true });
      }
      ['pointerdown', 'pointerenter', 'focusin', 'scroll'].forEach((type) => this.querySelector('.pdp__stage')?.addEventListener(type, wake, { once: true, passive: true }));
      this.track.addEventListener('scroll', wake, { once: true, passive: true });
    }

    // Drag with the mouse (touch and trackpads scroll natively). A real drag never opens the zoom.
    drag(el, onRelease) {
      if (!el) return;
      let startX = null;
      let startLeft = 0;
      let moved = 0;
      // the browser would start dragging the picture itself and end the gesture
      el.addEventListener('dragstart', (e) => e.preventDefault());
      el.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        startX = e.clientX;
        startLeft = el.scrollLeft;
        moved = 0;
        this.hydrate();
      });
      window.addEventListener('pointermove', (e) => {
        if (startX === null) return;
        moved = e.clientX - startX;
        if (Math.abs(moved) < 5) return;
        el.classList.add('is-dragging');
        el.scrollLeft = startLeft - moved;
      });
      window.addEventListener('pointerup', () => {
        if (startX === null) return;
        startX = null;
        const dragged = Math.abs(moved) >= 5;
        if (!dragged) {
          el.classList.remove('is-dragging');
          return;
        }
        this.suppressClick = true;
        setTimeout(() => {
          this.suppressClick = false;
        }, 0);
        if (!onRelease) {
          el.classList.remove('is-dragging');
          return;
        }
        // glide to the frame first and give the snapping back only afterwards — otherwise it pulls the photo back
        onRelease(moved);
        const done = () => el.classList.remove('is-dragging');
        el.addEventListener('scrollend', done, { once: true });
        setTimeout(done, 700);
      });
      el.addEventListener('click', (e) => {
        if (!this.suppressClick) return;
        e.preventDefault();
        e.stopImmediatePropagation();
      }, true);
    }

    hydrate() {
      if (this.hydrated) return;
      this.hydrated = true;
      this.querySelectorAll('img[data-src]').forEach((img) => {
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
      });
    }

    go(i, focus = false) {
      this.hydrate();
      const n = Math.max(0, Math.min(this.frames.length - 1, i));
      this.track.scrollTo({ left: n * this.track.clientWidth, behavior: this.still.matches ? 'auto' : 'smooth' });
      this.mark(n);
      if (focus) this.frames[n]?.focus({ preventScroll: true });
    }

    mark(n) {
      if (n < 0) return;
      this.index = n;
      this.frames.forEach((frame, i) => {
        frame.tabIndex = i === n ? 0 : -1;
      });
      this.bars.forEach((bar, i) => bar.classList.toggle('is-on', i <= n));
      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === n);
        if (i === n) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      // keep the active thumbnail in the strip's view — only the strip scrolls, never the page
      const active = this.thumbs[n];
      if (this.strip && active && !this.strip.classList.contains('is-dragging')) {
        const left = active.getBoundingClientRect().left - this.strip.getBoundingClientRect().left + this.strip.scrollLeft;
        const right = left + active.offsetWidth;
        if (left < this.strip.scrollLeft + 8) this.strip.scrollTo({ left: Math.max(0, left - 24), behavior: this.still.matches ? 'auto' : 'smooth' });
        else if (right > this.strip.scrollLeft + this.strip.clientWidth - 8) this.strip.scrollTo({ left: right - this.strip.clientWidth + 24, behavior: this.still.matches ? 'auto' : 'smooth' });
      }
      // the arrows flank the thumbnail row: at the ends they dim instead of vanishing, so the row never jumps
      if (this.prev) this.prev.disabled = n === 0;
      if (this.next) this.next.disabled = n === this.frames.length - 1;
    }

    // Zoom is a mandatory gallery element (Baymard): the shopper checks the knit and the seam before buying.
    // The dialog shows the frame at 2000 px, steps through the story and remembers where the shopper stopped.
    setupZoom() {
      const dialog = this.dialog;
      if (!dialog) return;
      this.zoomFrame = dialog.querySelector('[data-zoom-frame]');
      this.zoomCount = dialog.querySelector('[data-zoom-count]');
      this.zoomPrev = dialog.querySelector('[data-zoom-prev]');
      this.zoomNext = dialog.querySelector('[data-zoom-next]');
      this.zoomStrip = dialog.querySelector('[data-zoom-thumbs]');
      this.zoomPrev?.addEventListener('click', () => this.zoom(this.zoomIndex - 1));
      this.zoomNext?.addEventListener('click', () => this.zoom(this.zoomIndex + 1));
      dialog.addEventListener('keydown', (e) => {
        if (!dialog.open || (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft')) return;
        e.preventDefault();
        this.zoom(this.zoomIndex + (e.key === 'ArrowRight' ? 1 : -1));
      });
      dialog.addEventListener('close', () => {
        if (typeof this.zoomIndex === 'number' && this.zoomIndex !== this.index) this.go(this.zoomIndex);
      });
      const frame = this.zoomFrame;
      if (!frame) return;
      let startX = null;
      frame.addEventListener('pointerdown', (e) => {
        startX = frame.classList.contains('is-zoomed') ? null : e.clientX;
      });
      frame.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        startX = null;
        if (Math.abs(dx) > 48) {
          this.swiped = true;
          this.zoom(this.zoomIndex + (dx < 0 ? 1 : -1));
        }
      });
      frame.addEventListener('click', (e) => {
        if (this.swiped) {
          this.swiped = false;
          return;
        }
        if (!e.target.matches('[data-zoom-image]')) return;
        const before = frame.classList.contains('is-zoomed');
        const relX = (frame.scrollLeft + e.clientX - frame.getBoundingClientRect().left) / frame.scrollWidth;
        const relY = (frame.scrollTop + e.clientY - frame.getBoundingClientRect().top) / frame.scrollHeight;
        frame.classList.toggle('is-zoomed', !before);
        if (!before) {
          // Keep the tapped point in the centre — otherwise the zoom drifts away from the detail.
          frame.scrollLeft = relX * frame.scrollWidth - frame.clientWidth / 2;
          frame.scrollTop = relY * frame.scrollHeight - frame.clientHeight / 2;
        }
      });
    }

    zoom(i) {
      const dialog = this.dialog;
      const frame = this.zoomFrame;
      if (!dialog || !frame || !this.frames.length) return;
      const n = Math.max(0, Math.min(this.frames.length - 1, i));
      const source = this.frames[n];
      let img = frame.querySelector('[data-zoom-image]');
      if (!img) {
        img = document.createElement('img');
        img.setAttribute('data-zoom-image', '');
        frame.appendChild(img);
      }
      img.src = source.dataset.zoomSrc;
      img.alt = source.querySelector('img')?.alt || '';
      frame.classList.remove('is-zoomed');
      this.zoomIndex = n;
      const many = this.frames.length > 1;
      if (this.zoomCount) this.zoomCount.textContent = many ? `${n + 1} / ${this.frames.length}` : '';
      if (this.zoomPrev) this.zoomPrev.hidden = !many || n === 0;
      if (this.zoomNext) this.zoomNext.hidden = !many || n === this.frames.length - 1;
      // the gallery's thumbnails in the zoom too (the owner's words 16.10.2026); the active one follows the frame
      this.zoomThumbsBuild();
      (this.zoomThumbs || []).forEach((thumb, k) => {
        thumb.classList.toggle('is-active', k === n);
        if (k === n) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (!dialog.open) dialog.showModal();
      // keep the active thumbnail in view — only the strip scrolls
      const strip = this.zoomStrip;
      const active = this.zoomThumbs && this.zoomThumbs[n];
      if (strip && active && typeof strip.scrollTo === 'function') {
        const left = active.offsetLeft;
        const right = left + active.offsetWidth;
        const behavior = this.still.matches ? 'auto' : 'smooth';
        if (left < strip.scrollLeft + 8) strip.scrollTo({ left: Math.max(0, left - 24), behavior });
        else if (right > strip.scrollLeft + strip.clientWidth - 8) strip.scrollTo({ left: right - strip.clientWidth + 24, behavior });
      }
    }

    // The zoom's thumbnail strip: copies of the gallery thumbnails, made on the first open (the pictures are the ones
    // the page already has, nothing new is downloaded); a tap shows that frame in the zoom.
    zoomThumbsBuild() {
      const strip = this.zoomStrip;
      if (!strip || this.zoomThumbs || this.frames.length < 2 || this.thumbs.length !== this.frames.length) return;
      this.hydrate();
      this.zoomThumbs = this.thumbs.map((thumb, k) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'zoom__thumb';
        button.setAttribute('aria-label', thumb.getAttribute('aria-label') || String(k + 1));
        const img = thumb.querySelector('img');
        if (img) button.appendChild(img.cloneNode(true));
        button.addEventListener('click', () => this.zoom(k));
        strip.appendChild(button);
        return button;
      });
      strip.hidden = false;
      this.dialog.classList.add('has-thumbs');
    }
  }
  customElements.define('product-gallery', ProductGallery);

  class RecommendedProducts extends HTMLElement {
    async connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;
      try {
        const html = await (await fetch(url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('recommended-products');
        if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
      } catch {
        /* silent */
      }
    }
  }
  customElements.define('recommended-products', RecommendedProducts);

  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      // legacy selects (kept for markets that still render them): auto-submit on change
      this.querySelectorAll('[data-localization-submit]').forEach((select) => {
        select.addEventListener('change', () => this.form.submit());
      });
      // the visitor chose a country or a language by hand: the site never switches or suggests again (shop.md, «Страны и языки»)
      this.form.addEventListener('submit', () => {
        try {
          localStorage.setItem('df:locale-chosen', '1');
        } catch {
          /* silent */
        }
      });
      // the market picker (17.09.2026): chips and language links are plain submit buttons;
      // only the «many markets» dialog needs a hand — open, close on Escape/backdrop
      const dialog = this.querySelector('[data-market-dialog]');
      const opener = this.querySelector('[data-market-open]');
      if (dialog && opener && typeof dialog.showModal === 'function') {
        opener.addEventListener('click', () => dialog.showModal());
        dialog.querySelectorAll('[data-market-close]').forEach((b) => b.addEventListener('click', () => dialog.close()));
        dialog.addEventListener('click', (e) => {
          if (e.target === dialog) dialog.close();
        });
      }
    }
  }
  customElements.define('localization-form', LocalizationForm);

  // MarketSuggest — country and language decided for the visitor, quietly (shop.md, «Страны и языки»):
  // 1. a visitor from a country of another market gets one strip in the language of that country: «switch to your shop»;
  //    its «stay» button speaks the page's language — staying means this page (owner's words 16.10.2026);
  // 2. otherwise, on the first visit of the home page in a language other than the country's, the page switches silently
  //    to the language of the visitor's country (DE/AT → German, HR → Croatian, SI → Slovenian; others stay on English);
  // 3. otherwise one quiet strip offers the other useful language — the country's language on a page opened in another
  //    one (a deep link from Google or an ad), or the browser's language (a German with an English browser): the strip
  //    speaks the offered language and names it once.
  // One strip at most; a decision on a strip or a choice in the footer is remembered and nothing comes back.
  // Search robots are never switched. Shopify: browsing_context_suggestions.json gives the GeoIP country.
  class MarketSuggest extends HTMLElement {
    async connectedCallback() {
      const KEY = 'df:market-suggest';
      const store = {
        get(k) { try { return localStorage.getItem(k); } catch { return null; } },
        set(k, v) { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
        sget(k) { try { return sessionStorage.getItem(k); } catch { return null; } },
        sset(k, v) { try { sessionStorage.setItem(k, v); } catch { /* private mode */ } },
      };
      if (store.get('df:locale-chosen') || store.get(KEY)) return;
      const nav = typeof navigator !== 'undefined' ? navigator : {};
      const bot = nav.webdriver || /bot|crawl|spider|slurp|lighthouse|headless|preview/i.test(nav.userAgent || '');
      const current = (this.dataset.country || '').toUpperCase();
      const currentLang = (this.dataset.language || '').toLowerCase();
      const parse = (v, d) => {
        try {
          return JSON.parse(v || '') || d;
        } catch {
          return d;
        }
      };
      const countries = parse(this.dataset.countries, []);
      const availableLanguages = parse(this.dataset.languages, []);
      const texts = parse(this.dataset.texts, {});
      const langTexts = parse(this.dataset.langTexts, {});
      const root = (this.dataset.root || '/').replace(/\/?$/, '/');
      let detected = null;
      try {
        // routes.root_url is «/» for the default language but «/de» (no trailing slash) for the others
        const res = await fetch(`${root}browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true`, { headers: { Accept: 'application/json' } });
        if (res.ok) {
          const data = await res.json();
          const s = data && data.suggestions && data.suggestions[0] && data.suggestions[0].parts;
          detected = (data && data.detected_values && data.detected_values.country && data.detected_values.country.handle) || (s && s.country && s.country.handle) || null;
        }
      } catch {
        /* offline / blocked: fall back to the browser locale */
      }
      if (!detected) {
        const loc = (nav.language || '').split('-');
        if (loc[1]) detected = loc[1].toUpperCase();
      }
      // testing on an unpublished theme only: ?df_geo=DE stands in for the GeoIP country (ignored on the live theme)
      const forcedMatch = /[?&]df_geo=([A-Za-z]{2})/.exec((typeof location !== 'undefined' && location.search) || '');
      const forced = (forcedMatch && forcedMatch[1]) || store.sget('df:geo-test');
      const shopTheme = typeof window !== 'undefined' && window.Shopify && window.Shopify.theme;
      if (forced && shopTheme && shopTheme.role !== 'main') {
        detected = forced;
        store.sset('df:geo-test', forced);
      }
      detected = (detected || '').toUpperCase();
      const LANG_OF = { HR: 'hr', SI: 'sl', DE: 'de', AT: 'de', CH: 'de', LI: 'de', LU: 'de' };
      const NAME_OF = { hr: 'Hrvatski', sl: 'Slovenščina', de: 'Deutsch', en: 'English' };
      const has = (code) => Array.isArray(availableLanguages) && availableLanguages.some((l) => typeof l?.code === 'string' && l.code.toLowerCase() === code);
      const browserLang = ((nav.languages && nav.languages[0]) || nav.language || '').slice(0, 2).toLowerCase();
      const countryLang = LANG_OF[detected] || 'en';
      const market = countries.find((c) => c.code === detected);
      const groups = (this.dataset.groups || '').split('|').map((group) => group.split(',').map((code) => code.trim().toUpperCase()).filter(Boolean));
      const sameStore = market && (market.code === current || groups.some((group) => group.includes(current) && group.includes(market.code)));

      const decide = () => store.set(KEY, detected || '1');
      const switchTo = (country, language) => {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${root}localization`;
        form.hidden = true;
        const add = (name, value) => {
          const i = document.createElement('input');
          i.name = name;
          i.value = value;
          form.appendChild(i);
        };
        add('_method', 'PUT');
        if (country) add('country_code', country);
        if (language) add('language_code', language);
        add('return_to', location.pathname + location.search);
        document.body.appendChild(form);
        form.submit();
      };
      const show = (lang, html, short, label, accept, stay, onAccept, stayLang) => {
        const stayAttr = stayLang && stayLang !== lang ? ` lang="${stayLang}"` : '';
        this.innerHTML = `<div class="msuggest__in"><p class="msuggest__text">${html}</p><p class="msuggest__short">${short}</p><div class="msuggest__actions"><button type="button" class="btn btn--ink btn--sm" data-accept>${accept}</button><button type="button" class="msuggest__stay" data-stay${stayAttr}>${stay}</button></div></div>`;
        this.setAttribute('lang', lang);
        this.setAttribute('role', 'region');
        this.setAttribute('aria-label', label || '');
        this.hidden = false;
        this.querySelector('[data-stay]').addEventListener('click', () => { decide(); this.remove(); });
        this.querySelector('[data-accept]').addEventListener('click', () => { decide(); store.set('df:locale-chosen', '1'); onAccept(); });
      };

      // 1. another market: the strip in the language of the visitor's country
      if (market && !sameStore) {
        const lang = countryLang;
        const t = texts[lang] || texts.en;
        if (!t) return;
        const targetLanguage = has(countryLang) ? countryLang : null;
        const languageName = NAME_OF[lang] || '';
        const details = [languageName, market.currency].filter(Boolean).join(' · ');
        const fill = (str) => str
          .replace('{country}', `<b>${market.name}</b>`)
          .replace('{language} · {currency}', details)
          .replace('{language}', languageName)
          .replace('{currency}', market.currency || '');
        const stayLang = texts[currentLang] && texts[currentLang].stay ? currentLang : lang;
        // phones: one line «country · language · currency»; a part that does not fit whole is dropped (market-suggest.liquid)
        const parts = [languageName, market.currency].filter(Boolean).map((part) => `<span class="msuggest__part">&nbsp;· ${part}</span>`).join('');
        show(lang, fill(t.text), `<b>${market.name}</b>${parts}`, t.label, t.accept, (texts[stayLang] || t).stay, () => switchTo(market.code, targetLanguage), stayLang);
        return;
      }
      if (!market && !detected) return;

      // 2. first visit of the home page (once a session): switch silently to the country's language
      const isHome = location.pathname === '/' || location.pathname === root || location.pathname + '/' === root;
      if (!bot && isHome && countryLang !== currentLang && has(countryLang) && !store.sget('df:geo-auto')) {
        store.sset('df:geo-auto', '1');
        switchTo(null, countryLang);
        return;
      }

      // 3. one language strip: the country's language on a page in another one, else the browser's language
      let offer = null;
      if (countryLang !== currentLang && has(countryLang)) offer = countryLang;
      else if (browserLang && browserLang !== currentLang && has(browserLang)) offer = browserLang;
      if (!offer) return;
      const lt = langTexts[offer];
      if (!lt) return;
      show(offer, lt.text, lt.short || lt.text, lt.label, lt.accept, lt.stay, () => switchTo(null, offer));
    }
  }
  customElements.define('market-suggest', MarketSuggest);

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 220);
      });
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      try {
        const url = `${this.dataset.url}?q=${encodeURIComponent(q)}&resources[type]=product,article,page&section_id=predictive-search`;
        const html = await (await fetch(url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('.psr');
        this.results.innerHTML = inner ? inner.outerHTML : '';
      } catch {
        /* silent */
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  // Cookie consent — the design system's CookieBar, wired to Shopify's Customer Privacy API.
  // Opens only where the store asks for consent (shouldShowBanner); every choice goes to
  // setTrackingConsent, so the Shopify pixels (Meta, Google) follow it. [data-cookie-settings] reopens it.
  class CookieBar extends HTMLElement {
    connectedCallback() {
      this.set = this.querySelector('[data-cookie-set]');
      this.more = this.querySelector('[data-cookie-more]');
      this.all = this.querySelector('[data-cookie-all]');
      this.necessary = this.querySelector('[data-cookie-necessary]');
      this.save = this.querySelector('[data-cookie-save]');
      this.hide = this.querySelector('[data-cookie-hide]');
      this.all.addEventListener('click', () => this.decide({ preferences: true, analytics: true, marketing: true }));
      this.necessary.addEventListener('click', () => this.decide({ preferences: false, analytics: false, marketing: false }));
      this.save.addEventListener('click', () => this.decide(this.picked()));
      this.more.addEventListener('click', () => this.settings(true));
      this.hide.addEventListener('click', () => this.settings(false));
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-cookie-settings]');
        if (!opener) return;
        e.preventDefault();
        this.opener = opener;
        this.open(true);
      });
      this.privacy((api) => {
        // shouldShowBanner() only says the visitor's region needs consent — it stays true after a choice (measured on
        // the preview 20.10.2026), so open only while nothing has been decided yet
        if (!api.shouldShowBanner()) return;
        const now = api.currentVisitorConsent ? api.currentVisitorConsent() : {};
        const decided = ['preferences', 'analytics', 'marketing'].some((k) => now[k] === 'yes' || now[k] === 'no');
        if (!decided) this.open(false);
      });
    }
    privacy(done) {
      const shopify = window.Shopify;
      if (shopify && shopify.customerPrivacy) return done(shopify.customerPrivacy);
      if (!shopify || !shopify.loadFeatures) return;
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error && window.Shopify.customerPrivacy) done(window.Shopify.customerPrivacy);
      });
    }
    open(withSettings) {
      this.privacy((api) => {
        const now = api.currentVisitorConsent ? api.currentVisitorConsent() : {};
        this.querySelectorAll('[data-cookie-cat]').forEach((box) => {
          box.checked = now[box.dataset.cookieCat] === 'yes';
        });
      });
      this.hidden = false;
      this.settings(withSettings);
      if (withSettings) this.set.querySelector('input').focus();
    }
    // «Accept all» and the «Continue without accepting» link stay in both views; the settings swap
    // «Settings» for «Save choice» and «Hide».
    settings(show) {
      this.set.hidden = !show;
      this.more.hidden = show;
      this.save.hidden = !show;
      this.hide.hidden = !show;
      this.more.setAttribute('aria-expanded', String(show));
    }
    picked() {
      const choice = {};
      this.querySelectorAll('[data-cookie-cat]').forEach((box) => {
        choice[box.dataset.cookieCat] = box.checked;
      });
      return choice;
    }
    decide(choice) {
      this.privacy((api) => api.setTrackingConsent(choice, () => {}));
      this.hidden = true;
      if (this.opener) this.opener.focus();
      this.opener = null;
    }
  }
  customElements.define('cookie-bar', CookieBar);

  // Withdrawal form (EU online withdrawal, since 19.06.2026). On submit the statement gets the date and time in
  // Zagreb, so the shop's e-mail carries them; after the redirect the page shows the buyer what was sent and when.
  // The legal confirmation is still the shop's e-mail reply (site/legal/emails/withdrawal-receipt.md).
  class WithdrawalForm extends HTMLElement {
    static stamp(date) {
      const parts = new Intl.DateTimeFormat('de-DE', {
        timeZone: 'Europe/Zagreb', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(date).reduce((all, p) => ({ ...all, [p.type]: p.value }), {});
      return `${parts.day}.${parts.month}.${parts.year}, ${parts.hour}:${parts.minute} (Zagreb)`;
    }
    connectedCallback() {
      const receipt = this.querySelector('[data-withdrawal-receipt]');
      if (receipt) {
        this.showReceipt(receipt);
        return;
      }
      const form = this.querySelector('form');
      if (!form) return;
      form.addEventListener('submit', () => {
        const value = (name) => (form.querySelector(`[name="${name}"]`) || {}).value || '';
        const time = WithdrawalForm.stamp(new Date());
        const field = form.querySelector('[data-withdrawal-time]');
        if (field) field.value = time;
        const sent = { time, order: value('contact[Order]'), items: value('contact[Items]'), statement: value('contact[Statement]') };
        try { sessionStorage.setItem('df-withdrawal', JSON.stringify(sent)); } catch (e) { /* storage off: the e-mail still confirms */ }
      });
    }
    showReceipt(receipt) {
      let sent = null;
      try { sent = JSON.parse(sessionStorage.getItem('df-withdrawal') || 'null'); } catch (e) { sent = null; }
      if (!sent || !sent.time) return;
      const fill = (key, text) => {
        const slot = receipt.querySelector(`[data-receipt="${key}"]`);
        if (slot) slot.textContent = text;
      };
      fill('time', sent.time);
      fill('order', sent.order);
      fill('statement', sent.statement);
      fill('items', sent.items || receipt.dataset.allItems || '');
      receipt.hidden = false;
      try { sessionStorage.removeItem('df-withdrawal'); } catch (e) { /* nothing to clean */ }
    }
  }
  customElements.define('withdrawal-form', WithdrawalForm);

  document.addEventListener('click', (e) => {
    document.querySelectorAll('details[data-auto-close][open]').forEach((d) => {
      if (!d.contains(e.target)) d.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('details[data-auto-close][open]').forEach((d) => {
        // the keyboard was inside the closing panel (the search field): it goes back to the button that opened it
        const inside = d.contains(document.activeElement);
        d.removeAttribute('open');
        if (inside) d.querySelector('summary')?.focus();
      });
    }
  });
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (d.matches && d.matches('details[data-auto-close]') && d.open) {
      d.querySelector('input')?.focus();
    }
  }, true);

  document.addEventListener('click', async (e) => {
    const copy = e.target.closest('[data-copy-link]');
    if (!copy) return;
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(copy.dataset.copyLinkUrl || window.location.href);
      toast(`<span>${copy.dataset.copiedMessage || 'OK'}</span>`);
    } catch {
      window.prompt('URL', copy.dataset.copyLinkUrl || window.location.href);
    }
  });

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-dialog-open]');
    if (opener) {
      e.preventDefault();
      document.getElementById(opener.dataset.dialogOpen)?.showModal();
    }
    const closer = e.target.closest('[data-dialog-close]');
    if (closer) closer.closest('dialog')?.close();
  });

  const header = document.querySelector('[data-sticky-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // Dispatch days of the Zagreb warehouse: Monday to Friday except the days without dispatch (settings.
  // dispatch_closed_days: Croatian public holidays, 24 and 31 December), one cutoff for the whole shop
  // (settings.dispatch_cutoff, «13:00», Zagreb time). The countdown, the arrival days under the buy button and the
  // big guarantee block all count with this calendar.
  const isoDay = (d) => d.toISOString().slice(0, 10);
  const dispatchCalendar = (closedText, cutoffText) => {
    const closed = String(closedText || '').match(/\d{4}-\d{2}-\d{2}/g) || [];
    // the cutoff comes as «13:00» (or «13.5» / «13»); anything unreadable counts as 13:00
    const raw = String(cutoffText || '13:00');
    let cutoff = raw.includes(':') ? (+raw.split(':')[0]) * 60 + (+raw.split(':')[1] || 0) : Math.round(parseFloat(raw) * 60);
    if (!Number.isFinite(cutoff) || cutoff <= 0) cutoff = 13 * 60;
    const open = (d) => d.getUTCDay() !== 0 && d.getUTCDay() !== 6 && !closed.includes(isoDay(d));
    const after = (d, n) => {
      const out = new Date(d.getTime());
      for (let left = n; left > 0;) {
        out.setUTCDate(out.getUTCDate() + 1);
        if (open(out)) left -= 1;
      }
      return out;
    };
    return { cutoff, open, after };
  };
  // today in Zagreb (date at UTC midnight + minutes since midnight), the dispatch day and the minutes left before the cutoff
  const dispatchPlan = (now, cal) => {
    const p = {};
    new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Zagreb', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
      .formatToParts(now).forEach((x) => { p[x.type] = x.value; });
    const date = new Date(Date.UTC(+p.year, +p.month - 1, +p.day));
    const minutes = (+p.hour % 24) * 60 + (+p.minute);
    const left = cal.cutoff - minutes;
    const today = cal.open(date) && left > 0;
    return { date, minutes, left, today, dispatch: today ? date : cal.after(date, 1) };
  };

  // The live dispatch line and the arrival days (snippets/dispatch-clock). Honest by construction: «today» only on a
  // dispatch day before the cutoff, re-rendered every 30 s, so it turns into the next dispatch day at the cutoff
  // without a reload. Text nodes only: nothing from the data goes through innerHTML.
  class DispatchClock extends HTMLElement {
    static compute(now, d) {
      const cal = dispatchCalendar(d.closed, d.cutoff);
      const plan = dispatchPlan(now, cal);
      const days = (d.days || '').split(',');
      if (days.length !== 7) return null;
      if (d.part === 'arrival') {
        const min = parseInt(d.min, 10) || 0;
        const max = parseInt(d.max, 10) || 0;
        if (!min || max < min) return null;
        const first = cal.after(plan.dispatch, min);
        const last = cal.after(plan.dispatch, max);
        if (max === min) return { state: 'arrival', text: (d.one || '').replace('[day1]', days[first.getUTCDay()]) };
        // two neighbouring days: «Mi. oder Do.»; a wider window never names only its ends as «X or Y»: «Di.–Do.»
        const wide = max - min > 1;
        const list = wide ? (d.rangeDays || '').split(',') : days;
        const tpl = wide ? d.range : d.two;
        if (!tpl || list.length !== 7) return null;
        // the day pair never breaks across lines
        const text = tpl.replace(/\[day1\](.*?)\[day2\]/, (m, mid) => list[first.getUTCDay()] + mid.replace(/ /g, ' ') + list[last.getUTCDay()]);
        return { state: 'arrival', text };
      }
      if (!plan.today) return { state: 'later', text: (d.later || '').replace('[day]', days[plan.dispatch.getUTCDay()]) };
      const h = Math.floor(plan.left / 60);
      const m = plan.left % 60;
      const time = h && m ? (d.hm || '').replace('[h]', h).replace('[m]', m) : h ? (d.h || '').replace('[h]', h) : (d.m || '').replace('[m]', m);
      return { state: 'today', a: d.todayA || '', b: d.todayB || '', time };
    }
    connectedCallback() {
      this.render();
      this.tick = setInterval(() => this.render(), 30000);
    }
    disconnectedCallback() { clearInterval(this.tick); }
    render() {
      let r = null;
      try { r = DispatchClock.compute(new Date(), this.dataset); } catch { return; }
      if (!r) return;
      const suffix = this.dataset.suffix || '';
      this.dataset.state = r.state;
      if (r.state !== 'today') {
        this.textContent = r.text + (r.state === 'later' ? suffix : '');
        return;
      }
      // «In den nächsten 2 Std. 14 Min.» «bestellen –» «Versand noch heute»: up to three parts, so a line breaks only
      // between them — never «In den nächsten» alone. The time keeps the word before it (and the locale glues the words
      // that must not end a line, «u», «v», «In den»); a lone dash after the time stays with it.
      const nb = (t) => t.replace(/ /g, '\u00a0');
      const part = (cls, ...kids) => { const s = document.createElement('span'); s.className = cls; s.append(...kids); return s; };
      const [before, after = ''] = r.a.split('[time]');
      const rest = after.trim();
      const words = /[^\s–—-]/.test(rest);
      const first = part('dclock__a', before.replace(/ +$/, '\u00a0'), part('dclock__t', r.time), ...(words ? [] : [nb(after.trimEnd())]));
      const second = part('dclock__b', nb(r.b + suffix));
      this.replaceChildren(first, ' ', ...(words ? [part('dclock__m', nb(rest)), ' '] : []), second);
    }
  }
  customElements.define('dispatch-clock', DispatchClock);

  // The big guarantee block (snippets/guarantee.liquid, variant block): a timeline from today. Receipt = the dispatch
  // day (the theme's one cutoff, the same working days and holidays as the arrival line) + the country's MINIMUM
  // delivery days, so the shown end of the 100 days is never later than the real one. Always «about» and a part of
  // the month, never an exact end day; the 2 years as month and year. Without this script the block shows no dates.
  document.querySelectorAll('[data-gtl]').forEach((box) => {
    const months = (box.dataset.months || '').split(',');
    const parts = (box.dataset.parts || '').split('|');
    if (months.length !== 12 || parts.length !== 3) return;
    const cal = dispatchCalendar(box.dataset.closed, box.dataset.cutoff);
    let clock;
    try {
      clock = dispatchPlan(new Date(), cal);
    } catch {
      return;
    }
    const receipt = cal.after(clock.dispatch, Math.max(parseInt(box.dataset.days, 10) || 1, 1));
    const end100 = new Date(receipt.getTime());
    end100.setUTCDate(end100.getUTCDate() + 100);
    const end2y = new Date(receipt.getTime());
    end2y.setUTCFullYear(end2y.getUTCFullYear() + 2);
    const fill = (tpl, d) => tpl.replace('[day]', d.getUTCDate()).replace('[month]', months[d.getUTCMonth()]).replace('[year]', d.getUTCFullYear());
    const part = (d) => parts[d.getUTCDate() <= 10 ? 0 : d.getUTCDate() <= 20 ? 1 : 2];
    // [date] in bold; text nodes only, nothing from the data goes through innerHTML
    const put = (el, tpl, text) => {
      const [a, b = ''] = tpl.split('[date]');
      const strong = document.createElement('b');
      strong.textContent = text;
      el.replaceChildren(document.createTextNode(a), strong, document.createTextNode(b));
      el.hidden = false;
    };
    const todayEl = box.querySelector('[data-gtl-today]');
    if (todayEl && box.dataset.today && box.dataset.day) {
      const t = document.createElement('time');
      t.dateTime = isoDay(clock.date);
      t.textContent = fill(box.dataset.day, clock.date);
      const [a, b = ''] = box.dataset.today.split('[date]');
      todayEl.replaceChildren(document.createTextNode(a), t, document.createTextNode(b));
      todayEl.closest('li').hidden = false;
    }
    const w100 = box.querySelector('[data-gtl-when="100"]');
    if (w100 && box.dataset.until) put(w100, box.dataset.until, fill(part(end100), end100));
    const w2y = box.querySelector('[data-gtl-when="2y"]');
    if (w2y && box.dataset.until && box.dataset.monthYear) put(w2y, box.dataset.until, fill(box.dataset.monthYear, end2y));
    box.classList.add('gtl--dated');
  });

  const stickyAtc = document.querySelector('[data-sticky-atc]');
  const buyBox = document.querySelector('.pdp__buy');
  if (stickyAtc && buyBox) {
    let ticking = false;
    let room = 0;
    // the room under the page, the cookie card and the focus stop follow the bar's real height (77 px on one line,
    // about 100 px on a 360 px phone, more when a longer button word wraps)
    const measure = () => {
      const h = stickyAtc.offsetHeight;
      if (!h || h === room) return;
      room = h;
      document.documentElement.style.setProperty('--sticky-bar-h', `${h}px`);
    };
    const sync = () => {
      ticking = false;
      // visible whenever the buy box is off the screen: before it (the first screen of a phone shows the photos and
      // the name, the bar adds the price and the button) and after it — on a computer too, where the buy box is on
      // the first screen, so the bar comes after it
      const box = buyBox.getBoundingClientRect();
      const show = box.bottom < 0 || box.top > window.innerHeight;
      // a computer with «Also on computers» off draws no bar: then the page makes no room for it either
      const drawn = show && window.getComputedStyle(stickyAtc).display !== 'none';
      stickyAtc.classList.toggle('is-visible', show);
      stickyAtc.setAttribute('aria-hidden', drawn ? 'false' : 'true');
      stickyAtc.inert = !drawn;   // while hidden nothing inside takes the keyboard focus
      if (drawn) measure();
      document.body.classList.toggle('has-sticky-bar', drawn);
    };
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(stickyAtc);
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(sync);
    };
    sync();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // Wrong-choice guard (Р-117): until the shopper picks a size AND a pack
    // tier, the sticky CTA scrolls to the buy box instead of submitting.
    stickyAtc.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-add-button]');
      if (!btn) return;
      if (btn.dataset.mode === 'checkout') return;   // «Zur Kasse»: the button posts the StickyCheckout form
      const root = document.querySelector('[data-product-root]') || document;
      const needsSize = root.querySelector('variant-picker') && root.dataset.sizeOk !== '1';
      const tierPicker = root.querySelector('tier-picker');
      const needsTier = tierPicker && !tierPicker.chosen;
      if (needsSize || needsTier) {
        e.preventDefault();
        if (!showNeed(root)) buyBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });

    // «Zur Kasse» once this pack is in the cart (the owner's words 16.10.2026): the cart is read on load and after
    // every change (df:cart from syncCartCount); a new size, pack or purchase choice makes the button add again.
    const cartRoot = stickyAtc.closest('[data-product-root]');
    let cartIds = new Set();
    try {
      cartIds = new Set(JSON.parse(stickyAtc.dataset.cartIds || '[]').map(String));
    } catch {
      cartIds = new Set();
    }
    if (cartRoot && cartIds.size) {
      const markCart = (cart) => {
        if (!cart || !Array.isArray(cart.items)) return;
        cartRoot.dataset.inCart = cart.items.some((item) => cartIds.has(String(item.variant_id))) ? '1' : '';
        // while an add is on its way its own finally brings the buttons up to date
        if (!cartRoot.querySelector('product-form')?.busy) syncCta(cartRoot);
      };
      document.addEventListener('df:cart', (e) => markCart(e.detail));
      // captured before the pickers' own handlers run syncCta, so the new choice already counts
      cartRoot.querySelector('#ProductForm')?.addEventListener('change', () => { cartRoot.dataset.choiceFresh = '1'; }, true);
      fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })
        .then((res) => (res.ok ? res.json() : null))
        .then(markCart)
        .catch(() => {});
    }
  }
})();
